import { useEffect, useState } from "react";
import "./Insights.css";

const FINANCE_RECORD_LIMIT = 80;

function createDefaultFinanceWorkspace() {
  return {
    incomeRows: [],
    expenseRows: [],
    receivableRows: [],
    payableRows: [],
    invoiceRows: [],
    approvalRows: [],
    reconciliationRows: [],
    taxRows: [],
    budgetRows: [],
    reportRows: [],
    dailyBillRows: [],
    monthlyBillRows: [],
    yearlyBillRows: []
  };
}

function getFinanceTodayText() {
  return new Date().toISOString().slice(0, 10);
}

function readFinanceAmount(value) {
  const normalized = Number(String(value ?? "").replace(/[^0-9.-]/g, ""));
  return Number.isFinite(normalized) ? normalized : 0;
}

function normalizeFinanceWorkspace(value) {
  const next = value && typeof value === "object" ? value : {};
  return {
    incomeRows: Array.isArray(next.incomeRows) ? next.incomeRows : [],
    expenseRows: Array.isArray(next.expenseRows) ? next.expenseRows : [],
    receivableRows: Array.isArray(next.receivableRows) ? next.receivableRows : [],
    payableRows: Array.isArray(next.payableRows) ? next.payableRows : [],
    invoiceRows: Array.isArray(next.invoiceRows) ? next.invoiceRows : [],
    approvalRows: Array.isArray(next.approvalRows) ? next.approvalRows : [],
    reconciliationRows: Array.isArray(next.reconciliationRows) ? next.reconciliationRows : [],
    taxRows: Array.isArray(next.taxRows) ? next.taxRows : [],
    budgetRows: Array.isArray(next.budgetRows) ? next.budgetRows : [],
    reportRows: Array.isArray(next.reportRows) ? next.reportRows : [],
    dailyBillRows: Array.isArray(next.dailyBillRows) ? next.dailyBillRows : [],
    monthlyBillRows: Array.isArray(next.monthlyBillRows) ? next.monthlyBillRows : [],
    yearlyBillRows: Array.isArray(next.yearlyBillRows) ? next.yearlyBillRows : []
  };
}

function formatFinanceMonthLabel(value, locale) {
  const date = new Date(`${value}-01T00:00:00`);
  if (Number.isNaN(date.getTime())) {
    return value;
  }

  return date.toLocaleDateString(locale === "zh" ? "zh-CN" : "en-US", {
    year: "numeric",
    month: "short"
  });
}

function getFinanceMonthKey(value) {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return "";
  }

  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}`;
}

function getFinanceYearKey(value) {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return "";
  }

  return String(date.getFullYear());
}

function InsightMetric({ title, value, detail, tone = "blue" }) {
  return (
    <article className={`admin-insights-metric tone-${tone}`}>
      <span>{title}</span>
      <strong>{value}</strong>
      <small>{detail}</small>
    </article>
  );
}

function InsightPanel({ title, description, children, aside }) {
  return (
    <article className="admin-insights-panel">
      <div className="admin-insights-panel-head">
        <div>
          <h4>{title}</h4>
          <p>{description}</p>
        </div>
        {aside ? <div className="admin-insights-panel-aside">{aside}</div> : null}
      </div>
      {children}
    </article>
  );
}

function InsightBarList({ rows }) {
  const maximum = rows.reduce((maxValue, item) => Math.max(maxValue, Number(item.value || 0)), 1);

  return (
    <div className="admin-insights-bars">
      {rows.map((item) => (
        <div key={item.label} className="admin-insights-bar-row">
          <div className="admin-insights-bar-meta">
            <span>{item.label}</span>
            <strong>{item.value}</strong>
          </div>
          <div className="admin-insights-bar-track">
            <span style={{ width: `${Math.max(10, (Number(item.value || 0) / maximum) * 100)}%` }} />
          </div>
        </div>
      ))}
    </div>
  );
}

function InsightTimeline({ items }) {
  return (
    <div className="admin-insights-timeline">
      {items.map((item) => (
        <div key={item.title} className="admin-insights-timeline-item">
          <span className={`admin-insights-dot tone-${item.tone || "blue"}`} />
          <div>
            <strong>{item.title}</strong>
            <p>{item.detail}</p>
          </div>
        </div>
      ))}
    </div>
  );
}

export function AdminInsightsPage({
  adminTab,
  locale,
  adminOverview,
  adminOrders,
  adminUsers,
  operationsBanners,
  scopedWorkspaceMetrics,
  adminQuery,
  financeWorkspaceData,
  onSaveFinanceWorkspace
}) {
  const isZh = locale === "zh";
  const [financeWorkspace, setFinanceWorkspace] = useState(() => normalizeFinanceWorkspace(financeWorkspaceData));
  const [financeNotice, setFinanceNotice] = useState("");
  const [incomeDraft, setIncomeDraft] = useState(() => ({
    source: "",
    date: getFinanceTodayText(),
    amount: "",
    statusKey: "pending"
  }));
  const [expenseDraft, setExpenseDraft] = useState(() => ({
    item: "",
    date: getFinanceTodayText(),
    amount: "",
    owner: "",
    statusKey: "submitted"
  }));
  const [receivableDraft, setReceivableDraft] = useState(() => ({
    customer: "",
    source: "",
    dueDate: getFinanceTodayText(),
    amount: "",
    statusKey: "open"
  }));
  const [payableDraft, setPayableDraft] = useState(() => ({
    vendor: "",
    category: "",
    dueDate: getFinanceTodayText(),
    amount: "",
    statusKey: "pending"
  }));
  const [invoiceDraft, setInvoiceDraft] = useState(() => ({
    invoiceNo: "",
    type: "",
    related: "",
    amount: "",
    statusKey: "pending"
  }));
  const [reconciliationDraft, setReconciliationDraft] = useState(() => ({
    subject: "",
    amount: "",
    statusKey: "followup"
  }));
  const [taxDraft, setTaxDraft] = useState(() => ({
    taxItem: "",
    base: "",
    rate: "6",
    amount: "",
    dueDate: "",
    statusKey: "pending"
  }));
  const [budgetDraft, setBudgetDraft] = useState(() => ({
    department: "",
    budget: "",
    used: "",
    statusKey: "ontrack"
  }));
  const [reportDraft, setReportDraft] = useState(() => ({
    report: "",
    period: "",
    owner: "",
    export: "XLSX",
    statusKey: "ready"
  }));
  const [dailyBillDraft, setDailyBillDraft] = useState(() => ({
    date: getFinanceTodayText(),
    income: "",
    expense: "",
    statusKey: "draft"
  }));
  const [monthlyBillDraft, setMonthlyBillDraft] = useState(() => ({
    period: getFinanceTodayText().slice(0, 7),
    income: "",
    expense: "",
    statusKey: "draft"
  }));
  const [yearlyBillDraft, setYearlyBillDraft] = useState(() => ({
    year: String(new Date().getFullYear()),
    income: "",
    expense: "",
    tax: "",
    statusKey: "draft"
  }));
  const [approvalDraft, setApprovalDraft] = useState(() => ({
    type: "",
    subject: "",
    amount: "",
    owner: "",
    statusKey: "pending"
  }));
  const totalRevenue = Number(scopedWorkspaceMetrics?.revenueUSD ?? adminOverview?.revenueUSD ?? 0) || 0;
  const bannerCount = Number(scopedWorkspaceMetrics?.bannerCount || operationsBanners?.length || 0) || 0;
  const orderCount = Number(scopedWorkspaceMetrics?.orderCount || adminOrders.length || 0) || 0;
  const customerCount = Number(scopedWorkspaceMetrics?.customerCount || adminUsers.length || 0) || 0;
  const navCount = Number(scopedWorkspaceMetrics?.navItemCount || 0) || 0;
  const money = (value) => `US $${Number(value || 0).toFixed(2)}`;
  const financeStatusLabels = {
    income: {
      pending: isZh ? "待核销" : "Pending check",
      recognized: isZh ? "已确认" : "Recognized"
    },
    expense: {
      draft: isZh ? "草稿" : "Draft",
      submitted: isZh ? "待审批" : "Submitted",
      approved: isZh ? "已批准" : "Approved",
      booked: isZh ? "已入账" : "Booked"
    },
    receivable: {
      open: isZh ? "待跟进" : "Open",
      followup: isZh ? "催收中" : "Follow-up",
      collected: isZh ? "已收回" : "Collected"
    },
    payable: {
      pending: isZh ? "待审批" : "Pending",
      approved: isZh ? "已批准" : "Approved",
      paid: isZh ? "已付款" : "Paid"
    },
    invoice: {
      pending: isZh ? "待开票" : "Pending",
      issued: isZh ? "已开具" : "Issued",
      archived: isZh ? "已归档" : "Archived"
    },
    approval: {
      pending: isZh ? "待审批" : "Pending",
      reviewing: isZh ? "复核中" : "Reviewing",
      approved: isZh ? "已通过" : "Approved"
    },
    reconciliation: {
      followup: isZh ? "待跟进" : "Follow-up",
      matched: isZh ? "已匹配" : "Matched",
      review: isZh ? "待复核" : "Review"
    },
    tax: {
      pending: isZh ? "待复核" : "Pending",
      reviewed: isZh ? "已复核" : "Reviewed",
      filed: isZh ? "已申报" : "Filed"
    },
    budget: {
      ontrack: isZh ? "正常" : "On track",
      warning: isZh ? "超支预警" : "Warning",
      closed: isZh ? "已结转" : "Closed"
    },
    report: {
      ready: isZh ? "可导出" : "Ready",
      closing: isZh ? "待结账" : "Closing",
      generating: isZh ? "生成中" : "Generating"
    },
    summary: {
      draft: isZh ? "草稿" : "Draft",
      reviewed: isZh ? "已复核" : "Reviewed",
      closed: isZh ? "已结转" : "Closed"
    }
  };
  const financeStatusFlow = {
    income: ["pending", "recognized"],
    expense: ["draft", "submitted", "approved", "booked"],
    receivable: ["open", "followup", "collected"],
    payable: ["pending", "approved", "paid"],
    invoice: ["pending", "issued", "archived"],
    approval: ["pending", "reviewing", "approved"],
    reconciliation: ["followup", "matched", "review"],
    tax: ["pending", "reviewed", "filed"],
    budget: ["ontrack", "warning", "closed"],
    report: ["ready", "closing", "generating"],
    daily: ["draft", "reviewed", "closed"],
    monthly: ["draft", "reviewed", "closed"],
    yearly: ["draft", "reviewed", "closed"]
  };
  const getFinanceNextStatus = (section, statusKey) => {
    const sequence = financeStatusFlow[section] || [];
    const currentIndex = sequence.indexOf(statusKey);
    if (currentIndex < 0 || currentIndex >= sequence.length - 1) {
      return "";
    }

    return sequence[currentIndex + 1];
  };
  useEffect(() => {
    setFinanceWorkspace(normalizeFinanceWorkspace(financeWorkspaceData));
  }, [financeWorkspaceData]);

  const resolveOrderValue = (item) => {
    const rawValue = Number(item?.totalAmount ?? item?.total ?? item?.totalPrice ?? item?.amount ?? item?.orderAmount ?? 0);
    return Number.isFinite(rawValue) ? rawValue : 0;
  };
  const appendFinanceRecord = (section, payload) => {
    const keyBySection = {
      income: "incomeRows",
      expense: "expenseRows",
      receivable: "receivableRows",
      payable: "payableRows",
      invoice: "invoiceRows",
      approval: "approvalRows",
      reconciliation: "reconciliationRows",
      tax: "taxRows",
      budget: "budgetRows",
      report: "reportRows",
      daily: "dailyBillRows",
      monthly: "monthlyBillRows",
      yearly: "yearlyBillRows"
    };
    const targetKey = keyBySection[section];
    if (!targetKey) {
      return;
    }

    const nextWorkspace = (() => {
      const current = normalizeFinanceWorkspace(financeWorkspace);
      return {
        ...current,
        [targetKey]: [
          {
            id: `${section}-${Date.now()}-${Math.round(Math.random() * 1000)}`,
            ...payload
          },
          ...(current[targetKey] || [])
        ].slice(0, FINANCE_RECORD_LIMIT)
      };
    })();

    setFinanceWorkspace(nextWorkspace);
    if (typeof onSaveFinanceWorkspace === "function") {
      Promise.resolve(onSaveFinanceWorkspace(nextWorkspace)).catch((err) => {
        setFinanceNotice(err?.message || (isZh ? "财务台账保存失败。" : "Finance workspace save failed."));
      });
    }
  };
  const advanceFinanceRecord = (section, recordId) => {
    const keyBySection = {
      income: "incomeRows",
      expense: "expenseRows",
      receivable: "receivableRows",
      payable: "payableRows",
      invoice: "invoiceRows",
      approval: "approvalRows",
      reconciliation: "reconciliationRows",
      tax: "taxRows",
      budget: "budgetRows",
      report: "reportRows",
      daily: "dailyBillRows",
      monthly: "monthlyBillRows",
      yearly: "yearlyBillRows"
    };
    const targetKey = keyBySection[section];
    if (!targetKey) {
      return;
    }

    const nextWorkspace = (() => {
      const current = normalizeFinanceWorkspace(financeWorkspace);
      return {
        ...current,
        [targetKey]: (current[targetKey] || []).map((item) => {
          if (String(item?.id || "") !== String(recordId || "")) {
            return item;
          }

          const nextStatusKey = getFinanceNextStatus(section, String(item?.statusKey || ""));
          return nextStatusKey
            ? {
                ...item,
                statusKey: nextStatusKey
              }
            : item;
        })
      };
    })();

    setFinanceWorkspace(nextWorkspace);
    if (typeof onSaveFinanceWorkspace === "function") {
      Promise.resolve(onSaveFinanceWorkspace(nextWorkspace)).catch((err) => {
        setFinanceNotice(err?.message || (isZh ? "财务台账保存失败。" : "Finance workspace save failed."));
      });
    }
  };
  const submitIncomeDraft = () => {
    const amount = readFinanceAmount(incomeDraft.amount);
    if (!String(incomeDraft.source || "").trim() || amount <= 0) {
      setFinanceNotice(isZh ? "请填写入账来源和有效金额。" : "Provide an income source and a valid amount.");
      return;
    }

    appendFinanceRecord("income", {
      source: String(incomeDraft.source || "").trim(),
      date: String(incomeDraft.date || getFinanceTodayText()),
      amount,
      statusKey: String(incomeDraft.statusKey || "pending")
    });
    setIncomeDraft({
      source: "",
      date: getFinanceTodayText(),
      amount: "",
      statusKey: "pending"
    });
    setFinanceNotice(isZh ? "已新增一条入账流水。" : "Income record added.");
  };
  const submitExpenseDraft = () => {
    const amount = readFinanceAmount(expenseDraft.amount);
    if (!String(expenseDraft.item || "").trim() || !String(expenseDraft.owner || "").trim() || amount <= 0) {
      setFinanceNotice(isZh ? "请填写支出项目、归属部门和有效金额。" : "Provide an expense item, owner, and a valid amount.");
      return;
    }

    appendFinanceRecord("expense", {
      item: String(expenseDraft.item || "").trim(),
      date: String(expenseDraft.date || getFinanceTodayText()),
      amount,
      owner: String(expenseDraft.owner || "").trim(),
      statusKey: String(expenseDraft.statusKey || "submitted")
    });
    setExpenseDraft({
      item: "",
      date: getFinanceTodayText(),
      amount: "",
      owner: "",
      statusKey: "submitted"
    });
    setFinanceNotice(isZh ? "已新增一条支出账单。" : "Expense record added.");
  };
  const submitReceivableDraft = () => {
    const amount = readFinanceAmount(receivableDraft.amount);
    if (!String(receivableDraft.customer || "").trim() || !String(receivableDraft.source || "").trim() || amount <= 0) {
      setFinanceNotice(isZh ? "请填写客户、应收单号和有效金额。" : "Provide a customer, receivable number, and a valid amount.");
      return;
    }

    appendFinanceRecord("receivable", {
      customer: String(receivableDraft.customer || "").trim(),
      source: String(receivableDraft.source || "").trim(),
      dueDate: String(receivableDraft.dueDate || getFinanceTodayText()),
      amount,
      statusKey: String(receivableDraft.statusKey || "open")
    });
    setReceivableDraft({
      customer: "",
      source: "",
      dueDate: getFinanceTodayText(),
      amount: "",
      statusKey: "open"
    });
    setFinanceNotice(isZh ? "已新增一条应收账款。" : "Receivable added.");
  };
  const submitPayableDraft = () => {
    const amount = readFinanceAmount(payableDraft.amount);
    if (!String(payableDraft.vendor || "").trim() || !String(payableDraft.category || "").trim() || amount <= 0) {
      setFinanceNotice(isZh ? "请填写供应商、应付类型和有效金额。" : "Provide a vendor, payable type, and a valid amount.");
      return;
    }

    appendFinanceRecord("payable", {
      vendor: String(payableDraft.vendor || "").trim(),
      category: String(payableDraft.category || "").trim(),
      dueDate: String(payableDraft.dueDate || getFinanceTodayText()),
      amount,
      statusKey: String(payableDraft.statusKey || "pending")
    });
    setPayableDraft({
      vendor: "",
      category: "",
      dueDate: getFinanceTodayText(),
      amount: "",
      statusKey: "pending"
    });
    setFinanceNotice(isZh ? "已新增一条应付账款。" : "Payable added.");
  };
  const submitInvoiceDraft = () => {
    const amount = readFinanceAmount(invoiceDraft.amount);
    if (!String(invoiceDraft.invoiceNo || "").trim() || !String(invoiceDraft.type || "").trim() || !String(invoiceDraft.related || "").trim() || amount <= 0) {
      setFinanceNotice(isZh ? "请填写发票号、类型、关联业务和有效金额。" : "Provide an invoice number, type, related business, and a valid amount.");
      return;
    }

    appendFinanceRecord("invoice", {
      invoiceNo: String(invoiceDraft.invoiceNo || "").trim(),
      type: String(invoiceDraft.type || "").trim(),
      related: String(invoiceDraft.related || "").trim(),
      amount,
      statusKey: String(invoiceDraft.statusKey || "pending")
    });
    setInvoiceDraft({
      invoiceNo: "",
      type: "",
      related: "",
      amount: "",
      statusKey: "pending"
    });
    setFinanceNotice(isZh ? "已新增一条发票记录。" : "Invoice record added.");
  };
  const submitReconciliationDraft = () => {
    const amount = readFinanceAmount(reconciliationDraft.amount);
    if (!String(reconciliationDraft.subject || "").trim() || amount <= 0) {
      setFinanceNotice(isZh ? "请填写对账科目和有效金额。" : "Provide a reconciliation subject and a valid amount.");
      return;
    }

    appendFinanceRecord("reconciliation", {
      subject: String(reconciliationDraft.subject || "").trim(),
      amount,
      statusKey: String(reconciliationDraft.statusKey || "followup")
    });
    setReconciliationDraft({
      subject: "",
      amount: "",
      statusKey: "followup"
    });
    setFinanceNotice(isZh ? "已新增一条对账事项。" : "Reconciliation item added.");
  };
  const submitTaxDraft = () => {
    const base = readFinanceAmount(taxDraft.base);
    const rate = readFinanceAmount(taxDraft.rate);
    const amount = readFinanceAmount(taxDraft.amount);
    if (!String(taxDraft.taxItem || "").trim() || base < 0 || rate < 0 || amount < 0 || !String(taxDraft.dueDate || "").trim()) {
      setFinanceNotice(isZh ? "请填写税项、基数、税率、税额和处理节点。" : "Provide a tax item, base, rate, amount, and milestone.");
      return;
    }

    appendFinanceRecord("tax", {
      taxItem: String(taxDraft.taxItem || "").trim(),
      base,
      rate,
      amount,
      dueDate: String(taxDraft.dueDate || "").trim(),
      statusKey: String(taxDraft.statusKey || "pending")
    });
    setTaxDraft({
      taxItem: "",
      base: "",
      rate: "6",
      amount: "",
      dueDate: "",
      statusKey: "pending"
    });
    setFinanceNotice(isZh ? "已新增一条税务事项。" : "Tax row added.");
  };
  const submitBudgetDraft = () => {
    const budget = readFinanceAmount(budgetDraft.budget);
    const used = readFinanceAmount(budgetDraft.used);
    if (!String(budgetDraft.department || "").trim() || budget < 0 || used < 0) {
      setFinanceNotice(isZh ? "请填写部门、预算和已用金额。" : "Provide a department, budget, and used amount.");
      return;
    }

    appendFinanceRecord("budget", {
      department: String(budgetDraft.department || "").trim(),
      budget,
      used,
      statusKey: String(budgetDraft.statusKey || "ontrack")
    });
    setBudgetDraft({
      department: "",
      budget: "",
      used: "",
      statusKey: "ontrack"
    });
    setFinanceNotice(isZh ? "已新增一条预算记录。" : "Budget row added.");
  };
  const submitReportDraft = () => {
    if (!String(reportDraft.report || "").trim() || !String(reportDraft.period || "").trim() || !String(reportDraft.owner || "").trim() || !String(reportDraft.export || "").trim()) {
      setFinanceNotice(isZh ? "请填写报表、周期、负责人和导出格式。" : "Provide a report, period, owner, and export format.");
      return;
    }

    appendFinanceRecord("report", {
      report: String(reportDraft.report || "").trim(),
      period: String(reportDraft.period || "").trim(),
      owner: String(reportDraft.owner || "").trim(),
      export: String(reportDraft.export || "").trim(),
      statusKey: String(reportDraft.statusKey || "ready")
    });
    setReportDraft({
      report: "",
      period: "",
      owner: "",
      export: "XLSX",
      statusKey: "ready"
    });
    setFinanceNotice(isZh ? "已新增一条报表任务。" : "Report task added.");
  };
  const submitApprovalDraft = () => {
    const amount = readFinanceAmount(approvalDraft.amount);
    if (!String(approvalDraft.type || "").trim() || !String(approvalDraft.subject || "").trim() || !String(approvalDraft.owner || "").trim() || amount <= 0) {
      setFinanceNotice(isZh ? "请填写审批类型、事项、负责人和有效金额。" : "Provide an approval type, subject, owner, and a valid amount.");
      return;
    }

    appendFinanceRecord("approval", {
      type: String(approvalDraft.type || "").trim(),
      subject: String(approvalDraft.subject || "").trim(),
      amount,
      owner: String(approvalDraft.owner || "").trim(),
      statusKey: String(approvalDraft.statusKey || "pending")
    });
    setApprovalDraft({
      type: "",
      subject: "",
      amount: "",
      owner: "",
      statusKey: "pending"
    });
    setFinanceNotice(isZh ? "已新增一条审批事项。" : "Approval item added.");
  };

  useEffect(() => {
    if (!financeNotice) {
      return undefined;
    }

    const timer = window.setTimeout(() => {
      setFinanceNotice("");
    }, 2600);

    return () => window.clearTimeout(timer);
  }, [financeNotice]);

  const customIncomeRows = (financeWorkspace.incomeRows || []).map((item, index) => ({
    id: String(item?.id || `income-${index + 1}`),
    source: String(item?.source || "").trim(),
    date: String(item?.date || getFinanceTodayText()),
    amount: readFinanceAmount(item?.amount),
    statusKey: String(item?.statusKey || "pending")
  })).filter((item) => item.source && item.amount > 0);
  const customExpenseRows = (financeWorkspace.expenseRows || []).map((item, index) => ({
    id: String(item?.id || `expense-${index + 1}`),
    item: String(item?.item || "").trim(),
    date: String(item?.date || getFinanceTodayText()),
    amount: readFinanceAmount(item?.amount),
    owner: String(item?.owner || "").trim(),
    statusKey: String(item?.statusKey || "submitted")
  })).filter((item) => item.item && item.owner && item.amount > 0);
  const customReceivableRows = (financeWorkspace.receivableRows || []).map((item, index) => ({
    id: String(item?.id || `receivable-${index + 1}`),
    customer: String(item?.customer || "").trim(),
    source: String(item?.source || "").trim(),
    dueDate: String(item?.dueDate || getFinanceTodayText()),
    amount: readFinanceAmount(item?.amount),
    statusKey: String(item?.statusKey || "open")
  })).filter((item) => item.customer && item.source && item.amount > 0);
  const customPayableRows = (financeWorkspace.payableRows || []).map((item, index) => ({
    id: String(item?.id || `payable-${index + 1}`),
    vendor: String(item?.vendor || "").trim(),
    category: String(item?.category || "").trim(),
    dueDate: String(item?.dueDate || getFinanceTodayText()),
    amount: readFinanceAmount(item?.amount),
    statusKey: String(item?.statusKey || "pending")
  })).filter((item) => item.vendor && item.category && item.amount > 0);
  const customInvoiceRows = (financeWorkspace.invoiceRows || []).map((item, index) => ({
    id: String(item?.id || `invoice-${index + 1}`),
    invoiceNo: String(item?.invoiceNo || "").trim(),
    type: String(item?.type || "").trim(),
    related: String(item?.related || "").trim(),
    amount: readFinanceAmount(item?.amount),
    statusKey: String(item?.statusKey || "pending")
  })).filter((item) => item.invoiceNo && item.type && item.related && item.amount > 0);
  const customApprovalRows = (financeWorkspace.approvalRows || []).map((item, index) => ({
    id: String(item?.id || `approval-${index + 1}`),
    type: String(item?.type || "").trim(),
    subject: String(item?.subject || "").trim(),
    amount: readFinanceAmount(item?.amount),
    owner: String(item?.owner || "").trim(),
    statusKey: String(item?.statusKey || "pending")
  })).filter((item) => item.type && item.subject && item.owner && item.amount > 0);
  const customReconciliationRows = (financeWorkspace.reconciliationRows || []).map((item, index) => ({
    id: String(item?.id || `reconciliation-${index + 1}`),
    subject: String(item?.subject || "").trim(),
    amount: readFinanceAmount(item?.amount),
    statusKey: String(item?.statusKey || "followup")
  })).filter((item) => item.subject && item.amount > 0);
  const customTaxRows = (financeWorkspace.taxRows || []).map((item, index) => ({
    id: String(item?.id || `tax-${index + 1}`),
    taxItem: String(item?.taxItem || "").trim(),
    base: readFinanceAmount(item?.base),
    rate: readFinanceAmount(item?.rate),
    amount: readFinanceAmount(item?.amount),
    dueDate: String(item?.dueDate || "").trim(),
    statusKey: String(item?.statusKey || "pending")
  })).filter((item) => item.taxItem && item.dueDate && item.base >= 0 && item.rate >= 0 && item.amount >= 0);
  const customBudgetRows = (financeWorkspace.budgetRows || []).map((item, index) => ({
    id: String(item?.id || `budget-${index + 1}`),
    department: String(item?.department || "").trim(),
    budget: readFinanceAmount(item?.budget),
    used: readFinanceAmount(item?.used),
    statusKey: String(item?.statusKey || "ontrack")
  })).filter((item) => item.department && item.budget >= 0 && item.used >= 0);
  const customReportRows = (financeWorkspace.reportRows || []).map((item, index) => ({
    id: String(item?.id || `report-${index + 1}`),
    report: String(item?.report || "").trim(),
    period: String(item?.period || "").trim(),
    owner: String(item?.owner || "").trim(),
    export: String(item?.export || "").trim(),
    statusKey: String(item?.statusKey || "ready")
  })).filter((item) => item.report && item.period && item.owner && item.export);
  const orderRecognizedRevenue = adminOrders
    .filter((item) => !["cancelled", "refunded"].includes(String(item?.status || "").toLowerCase()))
    .reduce((sum, item) => sum + resolveOrderValue(item), 0) || totalRevenue;
  const totalCapturedIncome = customIncomeRows.reduce((sum, item) => sum + item.amount, 0);
  const recognizedIncome = customIncomeRows
    .filter((item) => item.statusKey === "recognized")
    .reduce((sum, item) => sum + item.amount, 0);
  const totalCapturedExpense = customExpenseRows.reduce((sum, item) => sum + item.amount, 0);
  const bookedExpense = customExpenseRows
    .filter((item) => ["approved", "booked"].includes(item.statusKey))
    .reduce((sum, item) => sum + item.amount, 0);
  const recognizedRevenue = customIncomeRows.length
    ? Math.max(recognizedIncome, totalCapturedIncome)
    : orderRecognizedRevenue;
  const estimatedExpenses = customExpenseRows.length
    ? Math.max(bookedExpense, totalCapturedExpense)
    : Math.round(recognizedRevenue * 0.28);
  const openApprovalAmount = customApprovalRows
    .filter((item) => item.statusKey !== "approved")
    .reduce((sum, item) => sum + item.amount, 0);
  const hasCustomFinanceActivity = customIncomeRows.length > 0 || customExpenseRows.length > 0;
  const today = new Date();
  const formatDateKey = (offsetDays) => {
    const date = new Date(today);
    date.setDate(date.getDate() - offsetDays);
    return date.toISOString().slice(0, 10);
  };
  const generatedIncomeRows = adminOrders
    .filter((item) => !["cancelled", "refunded"].includes(String(item?.status || "").toLowerCase()))
    .slice(0, 6)
    .map((item, index) => ({
      id: item?.id || `IN-${index + 1}`,
      source: isZh ? "订单回款" : "Order collection",
      date: formatDateKey(index),
      amount: money(resolveOrderValue(item) || recognizedRevenue / Math.max(orderCount || 1, 1)),
      status: isZh ? "已确认" : "Recognized"
    }));
  const fallbackIncomeRows = [
    { id: "IN-001", source: isZh ? "订单回款" : "Order collection", date: formatDateKey(0), amount: money(recognizedRevenue || 0), status: isZh ? "已确认" : "Recognized" },
    { id: "IN-002", source: isZh ? "样品费" : "Sample fee", date: formatDateKey(1), amount: money(Math.round(recognizedRevenue * 0.06)), status: isZh ? "待核销" : "Pending check" }
  ];
  const incomeRows = customIncomeRows.length
    ? customIncomeRows.map((item) => ({
        ...item,
        amount: money(item.amount),
        status: financeStatusLabels.income[item.statusKey] || item.statusKey,
        nextStatusKey: getFinanceNextStatus("income", item.statusKey)
      }))
    : (generatedIncomeRows.length ? generatedIncomeRows : fallbackIncomeRows);
  const generatedExpenseRows = [
    { item: isZh ? "采购成本" : "Purchasing cost", period: isZh ? "本月" : "This month", amount: money(Math.round(recognizedRevenue * 0.16)), owner: isZh ? "采购" : "Procurement", status: isZh ? "已入账" : "Booked" },
    { item: isZh ? "国际物流" : "International logistics", period: isZh ? "本月" : "This month", amount: money(Math.round(recognizedRevenue * 0.05)), owner: isZh ? "物流" : "Logistics", status: isZh ? "已入账" : "Booked" },
    { item: isZh ? "营销投放" : "Marketing spend", period: isZh ? "本月" : "This month", amount: money(Math.round(recognizedRevenue * 0.04)), owner: isZh ? "营销" : "Marketing", status: isZh ? "已入账" : "Booked" },
    { item: isZh ? "平台与手续费" : "Platform fees", period: isZh ? "本月" : "This month", amount: money(Math.round(recognizedRevenue * 0.02)), owner: isZh ? "财务" : "Finance", status: isZh ? "已入账" : "Booked" },
    { item: isZh ? "行政办公" : "Office admin", period: isZh ? "本月" : "This month", amount: money(Math.round(recognizedRevenue * 0.01)), owner: isZh ? "行政" : "Office", status: isZh ? "已入账" : "Booked" }
  ];
  const expenseRows = customExpenseRows.length
    ? customExpenseRows.map((item) => ({
        ...item,
        period: formatFinanceMonthLabel(getFinanceMonthKey(item.date) || item.date.slice(0, 7), locale),
        amount: money(item.amount),
        status: financeStatusLabels.expense[item.statusKey] || item.statusKey,
        nextStatusKey: getFinanceNextStatus("expense", item.statusKey)
      }))
    : generatedExpenseRows;
  const approvalRowsLive = customApprovalRows.length
    ? customApprovalRows.map((item) => ({
        ...item,
        amount: money(item.amount),
        status: financeStatusLabels.approval[item.statusKey] || item.statusKey,
        nextStatusKey: getFinanceNextStatus("approval", item.statusKey)
      }))
    : null;
  const defaultReceivableRows = [
    { customer: isZh ? "订单客户 A" : "Order customer A", source: "AR-001", dueDate: formatDateKey(0), aging: isZh ? "0-30 天" : "0-30 days", amount: money(Math.round(recognizedRevenue * 0.08)), action: isZh ? "本周催收" : "Collect this week" },
    { customer: isZh ? "渠道客户 B" : "Channel customer B", source: "AR-002", dueDate: formatDateKey(6), aging: isZh ? "31-60 天" : "31-60 days", amount: money(Math.round(recognizedRevenue * 0.06)), action: isZh ? "核对到账" : "Match receipt" },
    { customer: isZh ? "样品客户 C" : "Sample customer C", source: "AR-003", dueDate: formatDateKey(12), aging: isZh ? "预收转应收" : "Prepay to AR", amount: money(Math.round(recognizedRevenue * 0.04)), action: isZh ? "补发账单" : "Send statement" }
  ];
  const receivableRows = customReceivableRows.length
    ? customReceivableRows.map((item) => ({
        ...item,
        aging: isZh ? "待跟进" : "Open",
        amount: money(item.amount),
        action: financeStatusLabels.receivable[item.statusKey] || item.statusKey,
        nextStatusKey: getFinanceNextStatus("receivable", item.statusKey)
      }))
    : defaultReceivableRows;
  const defaultPayableRows = [
    { vendor: isZh ? "采购供应商" : "Procurement supplier", category: isZh ? "采购货款" : "Purchase payable", dueDate: formatDateKey(2), amount: money(Math.round(estimatedExpenses * 0.45)), status: isZh ? "待审批" : "Approval" },
    { vendor: isZh ? "国际货代" : "Freight forwarder", category: isZh ? "物流费用" : "Logistics fee", dueDate: formatDateKey(5), amount: money(Math.round(estimatedExpenses * 0.22)), status: isZh ? "待付款" : "Payment due" },
    { vendor: isZh ? "广告平台" : "Ad platform", category: isZh ? "营销费用" : "Marketing spend", dueDate: formatDateKey(9), amount: money(Math.round(estimatedExpenses * 0.16)), status: isZh ? "待复核" : "Review" }
  ];
  const payableRows = customPayableRows.length
    ? customPayableRows.map((item) => ({
        ...item,
        amount: money(item.amount),
        status: financeStatusLabels.payable[item.statusKey] || item.statusKey,
        nextStatusKey: getFinanceNextStatus("payable", item.statusKey)
      }))
    : defaultPayableRows;
  const defaultInvoiceRows = [
    { invoiceNo: "INV-001", type: isZh ? "销项发票" : "Sales invoice", related: isZh ? "订单回款" : "Order collection", amount: money(Math.round(recognizedRevenue * 0.22)), status: isZh ? "待开票" : "Pending issue" },
    { invoiceNo: "INV-002", type: isZh ? "进项发票" : "Input invoice", related: isZh ? "采购成本" : "Purchasing cost", amount: money(Math.round(estimatedExpenses * 0.38)), status: isZh ? "待收票" : "Awaiting receipt" },
    { invoiceNo: "INV-003", type: isZh ? "费用票据" : "Expense receipt", related: isZh ? "物流/营销" : "Logistics / marketing", amount: money(Math.round(estimatedExpenses * 0.18)), status: isZh ? "待归档" : "Archive" }
  ];
  const invoiceRows = customInvoiceRows.length
    ? customInvoiceRows.map((item) => ({
        ...item,
        amount: money(item.amount),
        status: financeStatusLabels.invoice[item.statusKey] || item.statusKey,
        nextStatusKey: getFinanceNextStatus("invoice", item.statusKey)
      }))
    : defaultInvoiceRows;
  const defaultTaxRows = [
    { taxItem: isZh ? "销项税预估" : "Output tax estimate", base: money(Math.round(recognizedRevenue * 0.82)), rate: "6%", amount: money(Math.round(recognizedRevenue * 0.049)), dueDate: isZh ? "本月申报" : "This filing" },
    { taxItem: isZh ? "进项税抵扣" : "Input tax credit", base: money(Math.round(estimatedExpenses * 0.55)), rate: "6%", amount: money(Math.round(estimatedExpenses * 0.033)), dueDate: isZh ? "待核验" : "Verify" },
    { taxItem: isZh ? "所得税预留" : "Income tax reserve", base: money(Math.max(0, recognizedRevenue - estimatedExpenses)), rate: "5%", amount: money(Math.round(Math.max(0, recognizedRevenue - estimatedExpenses) * 0.05)), dueDate: isZh ? "季度复核" : "Quarter review" }
  ];
  const taxRows = customTaxRows.length
    ? customTaxRows.map((item) => ({
        ...item,
        base: money(item.base),
        rate: `${item.rate}%`,
        amount: money(item.amount),
        nextStatusKey: getFinanceNextStatus("tax", item.statusKey)
      }))
    : defaultTaxRows;
  const defaultBudgetRows = [
    { department: isZh ? "采购" : "Procurement", budget: money(Math.round(recognizedRevenue * 0.18)), used: money(Math.round(recognizedRevenue * 0.16)), variance: "-11%", status: isZh ? "正常" : "On track" },
    { department: isZh ? "物流" : "Logistics", budget: money(Math.round(recognizedRevenue * 0.06)), used: money(Math.round(recognizedRevenue * 0.05)), variance: "-17%", status: isZh ? "正常" : "On track" },
    { department: isZh ? "营销" : "Marketing", budget: money(Math.round(recognizedRevenue * 0.035)), used: money(Math.round(recognizedRevenue * 0.04)), variance: "+14%", status: isZh ? "超支预警" : "Overspend alert" },
    { department: isZh ? "行政" : "Office", budget: money(Math.round(recognizedRevenue * 0.015)), used: money(Math.round(recognizedRevenue * 0.01)), variance: "-33%", status: isZh ? "正常" : "On track" }
  ];
  const budgetRows = customBudgetRows.length
    ? customBudgetRows.map((item) => ({
        ...item,
        budget: money(item.budget),
        used: money(item.used),
        variance: item.budget > 0 ? `${item.used > item.budget ? "+" : "-"}${Math.round((Math.abs(item.used - item.budget) / item.budget) * 100)}%` : "0%",
        status: financeStatusLabels.budget[item.statusKey] || item.statusKey,
        nextStatusKey: getFinanceNextStatus("budget", item.statusKey)
      }))
    : defaultBudgetRows;
  const defaultDailyBillRows = Array.from({ length: 7 }, (_, index) => {
    const income = Math.round((recognizedRevenue || 0) * (0.18 - index * 0.012));
    const expense = Math.round((estimatedExpenses || 0) * (0.2 - index * 0.01));
    return {
      date: formatDateKey(index),
      income: money(Math.max(0, income)),
      expense: money(Math.max(0, expense)),
      net: money(Math.max(0, income - expense))
    };
  });
  const monthLabels = isZh
    ? ["本月", "上月", "前2月", "前3月", "前4月", "前5月"]
    : ["This month", "Last month", "2 months ago", "3 months ago", "4 months ago", "5 months ago"];
  const defaultMonthlyBillRows = monthLabels.map((label, index) => {
    const income = Math.round((recognizedRevenue || 0) * (1 - index * 0.08));
    const expense = Math.round((estimatedExpenses || 0) * (1 - index * 0.06));
    return {
      period: label,
      income: money(Math.max(0, income)),
      expense: money(Math.max(0, expense)),
      profit: money(Math.max(0, income - expense))
    };
  });
  const defaultYearlyBillRows = [0, 1, 2].map((offset) => {
    const year = today.getFullYear() - offset;
    const income = Math.round((recognizedRevenue || 0) * (12 - offset * 1.4));
    const expense = Math.round((estimatedExpenses || 0) * (12 - offset * 1.1));
    const tax = Math.round(income * 0.06);
    return {
      year,
      income: money(Math.max(0, income)),
      expense: money(Math.max(0, expense)),
      tax: money(Math.max(0, tax)),
      net: money(Math.max(0, income - expense - tax))
    };
  });
  const dailyBillRows = hasCustomFinanceActivity
    ? Array.from({ length: 7 }, (_, index) => {
        const date = formatDateKey(index);
        const income = customIncomeRows
          .filter((item) => item.date === date)
          .reduce((sum, item) => sum + item.amount, 0);
        const expense = customExpenseRows
          .filter((item) => item.date === date)
          .reduce((sum, item) => sum + item.amount, 0);
        return {
          date,
          income: money(income),
          expense: money(expense),
          net: money(income - expense)
        };
      })
    : defaultDailyBillRows;
  const monthlyBillRows = hasCustomFinanceActivity
    ? Array.from({ length: 6 }, (_, index) => {
        const date = new Date(today.getFullYear(), today.getMonth() - index, 1);
        const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}`;
        const income = customIncomeRows
          .filter((item) => getFinanceMonthKey(item.date) === monthKey)
          .reduce((sum, item) => sum + item.amount, 0);
        const expense = customExpenseRows
          .filter((item) => getFinanceMonthKey(item.date) === monthKey)
          .reduce((sum, item) => sum + item.amount, 0);
        return {
          period: formatFinanceMonthLabel(monthKey, locale),
          income: money(income),
          expense: money(expense),
          profit: money(income - expense)
        };
      })
    : defaultMonthlyBillRows;
  const yearlyBillRows = hasCustomFinanceActivity
    ? [0, 1, 2].map((offset) => {
        const year = String(today.getFullYear() - offset);
        const income = customIncomeRows
          .filter((item) => getFinanceYearKey(item.date) === year)
          .reduce((sum, item) => sum + item.amount, 0);
        const expense = customExpenseRows
          .filter((item) => getFinanceYearKey(item.date) === year)
          .reduce((sum, item) => sum + item.amount, 0);
        const tax = Math.max(0, Math.round((income - expense) * 0.06));
        return {
          year,
          income: money(income),
          expense: money(expense),
          tax: money(tax),
          net: money(income - expense - tax)
        };
      })
    : defaultYearlyBillRows;
  const defaultReconciliationRows = [
    { subject: isZh ? "应收账款" : "Receivables", amount: money(Math.round(recognizedRevenue * 0.18)), status: isZh ? "待催收" : "Follow-up" },
    { subject: isZh ? "已收款" : "Collected", amount: money(Math.round(recognizedRevenue * 0.82)), status: isZh ? "已匹配" : "Matched" },
    { subject: isZh ? "应付款" : "Payables", amount: money(Math.round(estimatedExpenses * 0.45)), status: isZh ? "待审批" : "Approval" },
    { subject: isZh ? "待开票" : "Pending invoices", amount: money(Math.round(recognizedRevenue * 0.22)), status: isZh ? "待开票" : "Pending" },
    { subject: isZh ? "税费预估" : "Tax estimate", amount: money(Math.round(recognizedRevenue * 0.06)), status: isZh ? "需复核" : "Review" }
  ];
  const reconciliationRows = customReconciliationRows.length
    ? customReconciliationRows.map((item) => ({
        ...item,
        amount: money(item.amount),
        status: financeStatusLabels.reconciliation[item.statusKey] || item.statusKey,
        nextStatusKey: getFinanceNextStatus("reconciliation", item.statusKey)
      }))
    : defaultReconciliationRows;
  const approvalRows = [
    { type: isZh ? "付款审批" : "Payment approval", subject: isZh ? "采购货款" : "Purchase payment", amount: money(Math.round(estimatedExpenses * 0.45)), owner: isZh ? "财务经理" : "Finance manager", status: isZh ? "待审批" : "Pending" },
    { type: isZh ? "退款审批" : "Refund approval", subject: isZh ? "取消订单" : "Cancelled order", amount: money(Math.round(recognizedRevenue * 0.03)), owner: isZh ? "财务经理" : "Finance manager", status: isZh ? "待复核" : "Review" },
    { type: isZh ? "费用审计" : "Expense audit", subject: isZh ? "营销超支" : "Marketing overspend", amount: money(Math.round(recognizedRevenue * 0.005)), owner: isZh ? "部门负责人" : "Department owner", status: isZh ? "需说明" : "Need note" }
  ];
  const defaultReportRows = [
    { report: isZh ? "现金流日报" : "Cashflow daily", period: isZh ? "今日" : "Today", owner: isZh ? "财务专员" : "Finance specialist", status: isZh ? "可导出" : "Ready", export: "CSV / XLSX" },
    { report: isZh ? "月度利润表" : "Monthly P&L", period: isZh ? "本月" : "This month", owner: isZh ? "财务经理" : "Finance manager", status: isZh ? "待结账" : "Closing", export: "XLSX" },
    { report: isZh ? "年度经营报表" : "Annual operating report", period: String(today.getFullYear()), owner: isZh ? "管理层" : "Management", status: isZh ? "生成中" : "Generating", export: "PDF / XLSX" }
  ];
  const reportRows = customReportRows.length
    ? customReportRows.map((item) => ({
        ...item,
        status: financeStatusLabels.report[item.statusKey] || item.statusKey,
        nextStatusKey: getFinanceNextStatus("report", item.statusKey)
      }))
    : defaultReportRows;
  const financeModuleRows = [
    { module: isZh ? "收款与应收" : "Collections & AR", purpose: isZh ? "跟订单、到账和账龄" : "Track orders, receipts, and aging", owner: isZh ? "财务专员" : "Finance specialist", nextStep: isZh ? "优先催收到期款" : "Collect due balances" },
    { module: isZh ? "付款与应付" : "Payments & AP", purpose: isZh ? "控供应商付款节奏" : "Control supplier payment cadence", owner: isZh ? "财务经理" : "Finance manager", nextStep: isZh ? "审批到期付款" : "Approve due payments" },
    { module: isZh ? "发票税务" : "Invoices & Tax", purpose: isZh ? "管理票据和申报口径" : "Manage invoice and filing basis", owner: isZh ? "财税" : "Finance / tax", nextStep: isZh ? "复核开票缺口" : "Review invoice gaps" },
    { module: isZh ? "预算审计报表" : "Budget, Audit & Reports", purpose: isZh ? "看预算偏差和导出报表" : "Review variance and export reports", owner: isZh ? "管理层" : "Management", nextStep: isZh ? "月末结账复盘" : "Month-end close review" }
  ];
  const billColumns = {
    income: [
      { key: "id", label: isZh ? "流水号" : "Record ID" },
      { key: "source", label: isZh ? "入账来源" : "Source" },
      { key: "date", label: isZh ? "日期" : "Date" },
      { key: "amount", label: isZh ? "金额" : "Amount" },
      { key: "status", label: isZh ? "状态" : "Status" }
    ],
    expenses: [
      { key: "item", label: isZh ? "支出项目" : "Expense Item" },
      { key: "period", label: isZh ? "周期" : "Period" },
      { key: "amount", label: isZh ? "金额" : "Amount" },
      { key: "owner", label: isZh ? "归属部门" : "Owner" },
      { key: "status", label: isZh ? "状态" : "Status" }
    ],
    receivables: [
      { key: "customer", label: isZh ? "客户" : "Customer" },
      { key: "source", label: isZh ? "应收单号" : "AR No." },
      { key: "dueDate", label: isZh ? "到期日" : "Due Date" },
      { key: "aging", label: isZh ? "账龄" : "Aging" },
      { key: "amount", label: isZh ? "金额" : "Amount" },
      { key: "action", label: isZh ? "动作" : "Action" }
    ],
    payables: [
      { key: "vendor", label: isZh ? "供应商" : "Vendor" },
      { key: "category", label: isZh ? "应付类型" : "Payable Type" },
      { key: "dueDate", label: isZh ? "到期日" : "Due Date" },
      { key: "amount", label: isZh ? "金额" : "Amount" },
      { key: "status", label: isZh ? "状态" : "Status" }
    ],
    invoices: [
      { key: "invoiceNo", label: isZh ? "发票号" : "Invoice No." },
      { key: "type", label: isZh ? "类型" : "Type" },
      { key: "related", label: isZh ? "关联业务" : "Related Business" },
      { key: "amount", label: isZh ? "金额" : "Amount" },
      { key: "status", label: isZh ? "状态" : "Status" }
    ],
    tax: [
      { key: "taxItem", label: isZh ? "税项" : "Tax Item" },
      { key: "base", label: isZh ? "计税基数" : "Tax Base" },
      { key: "rate", label: isZh ? "税率" : "Rate" },
      { key: "amount", label: isZh ? "税额" : "Tax Amount" },
      { key: "dueDate", label: isZh ? "处理节点" : "Milestone" }
    ],
    budget: [
      { key: "department", label: isZh ? "部门" : "Department" },
      { key: "budget", label: isZh ? "预算" : "Budget" },
      { key: "used", label: isZh ? "已用" : "Used" },
      { key: "variance", label: isZh ? "偏差" : "Variance" },
      { key: "status", label: isZh ? "状态" : "Status" }
    ],
    daily: [
      { key: "date", label: isZh ? "日期" : "Date" },
      { key: "income", label: isZh ? "入账" : "Income" },
      { key: "expense", label: isZh ? "开销" : "Expense" },
      { key: "net", label: isZh ? "净额" : "Net" }
    ],
    monthly: [
      { key: "period", label: isZh ? "月份" : "Month" },
      { key: "income", label: isZh ? "入账" : "Income" },
      { key: "expense", label: isZh ? "开销" : "Expense" },
      { key: "profit", label: isZh ? "利润" : "Profit" }
    ],
    yearly: [
      { key: "year", label: isZh ? "年份" : "Year" },
      { key: "income", label: isZh ? "营收" : "Revenue" },
      { key: "expense", label: isZh ? "费用" : "Expense" },
      { key: "tax", label: isZh ? "税费" : "Tax" },
      { key: "net", label: isZh ? "净利润" : "Net Profit" }
    ],
    reconciliation: [
      { key: "subject", label: isZh ? "对账科目" : "Subject" },
      { key: "amount", label: isZh ? "金额" : "Amount" },
      { key: "status", label: isZh ? "状态" : "Status" }
    ],
    approvals: [
      { key: "type", label: isZh ? "审批类型" : "Approval Type" },
      { key: "subject", label: isZh ? "事项" : "Subject" },
      { key: "amount", label: isZh ? "金额" : "Amount" },
      { key: "owner", label: isZh ? "负责人" : "Owner" },
      { key: "status", label: isZh ? "状态" : "Status" }
    ],
    reports: [
      { key: "report", label: isZh ? "报表" : "Report" },
      { key: "period", label: isZh ? "周期" : "Period" },
      { key: "owner", label: isZh ? "负责人" : "Owner" },
      { key: "status", label: isZh ? "状态" : "Status" },
      { key: "export", label: isZh ? "导出格式" : "Export" }
    ],
    modules: [
      { key: "module", label: isZh ? "模块" : "Module" },
      { key: "purpose", label: isZh ? "用途" : "Purpose" },
      { key: "owner", label: isZh ? "负责人" : "Owner" },
      { key: "nextStep", label: isZh ? "下一步" : "Next Step" }
    ]
  };
  const incomeRecordColumns = [
    ...billColumns.income,
    {
      key: "actions",
      label: isZh ? "操作" : "Action",
      render: (row) => row.nextStatusKey ? (
        <button
          type="button"
          className="admin-finance-table-action"
          onClick={() => {
            advanceFinanceRecord("income", row.id);
          }}
        >
          {financeStatusLabels.income[row.nextStatusKey] || row.nextStatusKey}
        </button>
      ) : (
        <span className="admin-finance-table-static">{isZh ? "已完成" : "Done"}</span>
      )
    }
  ];
  const expenseRecordColumns = [
    ...billColumns.expenses,
    {
      key: "actions",
      label: isZh ? "操作" : "Action",
      render: (row) => row.nextStatusKey ? (
        <button
          type="button"
          className="admin-finance-table-action"
          onClick={() => {
            advanceFinanceRecord("expense", row.id);
          }}
        >
          {financeStatusLabels.expense[row.nextStatusKey] || row.nextStatusKey}
        </button>
      ) : (
        <span className="admin-finance-table-static">{isZh ? "已入账" : "Booked"}</span>
      )
    }
  ];
  const receivableRecordColumns = [
    ...billColumns.receivables.slice(0, -1),
    {
      key: "actions",
      label: isZh ? "操作" : "Action",
      render: (row) => row.nextStatusKey ? (
        <button
          type="button"
          className="admin-finance-table-action"
          onClick={() => {
            advanceFinanceRecord("receivable", row.id);
          }}
        >
          {financeStatusLabels.receivable[row.nextStatusKey] || row.nextStatusKey}
        </button>
      ) : (
        <span className="admin-finance-table-static">{isZh ? "已回款" : "Collected"}</span>
      )
    }
  ];
  const payableRecordColumns = [
    ...billColumns.payables,
    {
      key: "actions",
      label: isZh ? "操作" : "Action",
      render: (row) => row.nextStatusKey ? (
        <button
          type="button"
          className="admin-finance-table-action"
          onClick={() => {
            advanceFinanceRecord("payable", row.id);
          }}
        >
          {financeStatusLabels.payable[row.nextStatusKey] || row.nextStatusKey}
        </button>
      ) : (
        <span className="admin-finance-table-static">{isZh ? "已付款" : "Paid"}</span>
      )
    }
  ];
  const invoiceRecordColumns = [
    ...billColumns.invoices,
    {
      key: "actions",
      label: isZh ? "操作" : "Action",
      render: (row) => row.nextStatusKey ? (
        <button
          type="button"
          className="admin-finance-table-action"
          onClick={() => {
            advanceFinanceRecord("invoice", row.id);
          }}
        >
          {financeStatusLabels.invoice[row.nextStatusKey] || row.nextStatusKey}
        </button>
      ) : (
        <span className="admin-finance-table-static">{isZh ? "已归档" : "Archived"}</span>
      )
    }
  ];
  const approvalRecordColumns = [
    ...billColumns.approvals,
    {
      key: "actions",
      label: isZh ? "操作" : "Action",
      render: (row) => row.nextStatusKey ? (
        <button
          type="button"
          className="admin-finance-table-action"
          onClick={() => {
            advanceFinanceRecord("approval", row.id);
          }}
        >
          {financeStatusLabels.approval[row.nextStatusKey] || row.nextStatusKey}
        </button>
      ) : (
        <span className="admin-finance-table-static">{isZh ? "已通过" : "Approved"}</span>
      )
    }
  ];
  const reconciliationRecordColumns = [
    ...billColumns.reconciliation,
    {
      key: "actions",
      label: isZh ? "操作" : "Action",
      render: (row) => row.nextStatusKey ? (
        <button
          type="button"
          className="admin-finance-table-action"
          onClick={() => {
            advanceFinanceRecord("reconciliation", row.id);
          }}
        >
          {financeStatusLabels.reconciliation[row.nextStatusKey] || row.nextStatusKey}
        </button>
      ) : (
        <span className="admin-finance-table-static">{isZh ? "已完成" : "Done"}</span>
      )
    }
  ];
  const taxRecordColumns = [
    ...billColumns.tax,
    {
      key: "actions",
      label: isZh ? "操作" : "Action",
      render: (row) => row.nextStatusKey ? (
        <button
          type="button"
          className="admin-finance-table-action"
          onClick={() => {
            advanceFinanceRecord("tax", row.id);
          }}
        >
          {financeStatusLabels.tax[row.nextStatusKey] || row.nextStatusKey}
        </button>
      ) : (
        <span className="admin-finance-table-static">{isZh ? "已申报" : "Filed"}</span>
      )
    }
  ];
  const budgetRecordColumns = [
    ...billColumns.budget,
    {
      key: "actions",
      label: isZh ? "操作" : "Action",
      render: (row) => row.nextStatusKey ? (
        <button
          type="button"
          className="admin-finance-table-action"
          onClick={() => {
            advanceFinanceRecord("budget", row.id);
          }}
        >
          {financeStatusLabels.budget[row.nextStatusKey] || row.nextStatusKey}
        </button>
      ) : (
        <span className="admin-finance-table-static">{isZh ? "已结转" : "Closed"}</span>
      )
    }
  ];
  const reportRecordColumns = [
    ...billColumns.reports,
    {
      key: "actions",
      label: isZh ? "操作" : "Action",
      render: (row) => row.nextStatusKey ? (
        <button
          type="button"
          className="admin-finance-table-action"
          onClick={() => {
            advanceFinanceRecord("report", row.id);
          }}
        >
          {financeStatusLabels.report[row.nextStatusKey] || row.nextStatusKey}
        </button>
      ) : (
        <span className="admin-finance-table-static">{isZh ? "已生成" : "Generated"}</span>
      )
    }
  ];
  const financeWorkbenchSections = adminTab === "finance"
    ? ["income", "expense", "receivable", "payable", "invoice", "approval"]
    : adminTab === "financeIncome"
      ? ["income"]
      : adminTab === "financeExpenses"
        ? ["expense"]
        : adminTab === "financeReceivables"
          ? ["receivable"]
          : adminTab === "financePayables"
            ? ["payable"]
            : adminTab === "financeInvoices"
              ? ["invoice"]
              : adminTab === "financeReconciliation"
                ? ["reconciliation"]
                : adminTab === "financeTax"
                  ? ["tax"]
                  : adminTab === "financeBudget"
                    ? ["budget"]
                    : adminTab === "financeReports"
                      ? ["report"]
        : adminTab === "financeApprovals"
          ? ["approval"]
          : [];
  const shouldShowFinanceWorkbench = financeWorkbenchSections.length > 0;

  const tabConfigs = {
    marketing: {
      eyebrow: isZh ? "营销增长中心" : "Marketing Growth Center",
      title: isZh ? "营销中心" : "Marketing Center",
      description: isZh ? "把活动、素材和触达动作集中到一个运营视图里，便于快速判断本周该推什么。" : "Unify campaigns, creatives, and audience touchpoints in one operating view to decide what to push this week.",
      metrics: [
        { title: isZh ? "活动触点" : "Campaign Touchpoints", value: bannerCount + navCount, detail: isZh ? "轮播与导航入口总量" : "Combined banners and navigation entries", tone: "blue" },
        { title: isZh ? "曝光商品" : "Promoted SKUs", value: Math.max(0, Math.round(orderCount * 0.8)), detail: isZh ? "当前活动主推商品池" : "Featured products in campaign rotation", tone: "violet" },
        { title: isZh ? "预估营收" : "Projected Revenue", value: `US $${totalRevenue.toFixed(0)}`, detail: isZh ? "结合现有经营概览" : "Derived from current operating overview", tone: "emerald" },
        { title: isZh ? "客户触达" : "Audience Reach", value: customerCount, detail: isZh ? "可被活动覆盖的客户数" : "Customers addressable by current campaigns", tone: "amber" }
      ],
      bars: [
        { label: isZh ? "首页轮播" : "Homepage banners", value: bannerCount },
        { label: isZh ? "导航入口" : "Navigation entries", value: navCount },
        { label: isZh ? "订单转化" : "Order conversions", value: Math.max(1, Math.round(orderCount * 0.4)) },
        { label: isZh ? "复购机会" : "Repeat-buy opportunities", value: Math.max(1, Math.round(customerCount * 0.25)) }
      ],
      timeline: [
        { title: isZh ? "上午 10:00 调整首页轮播" : "10:00 refresh homepage banner", detail: isZh ? "替换高点击素材并同步按钮文案" : "Swap in higher-performing creative and CTA copy", tone: "blue" },
        { title: isZh ? "下午 14:00 检查促销入口" : "14:00 audit campaign entries", detail: isZh ? "确保导航、横幅与专题页跳转一致" : "Keep navigation, banners, and landing pages aligned", tone: "amber" },
        { title: isZh ? "晚间 18:00 复盘当日转化" : "18:00 review daily conversion", detail: isZh ? "看订单与询盘是否跟上流量变化" : "Compare orders and inquiries against traffic changes", tone: "emerald" }
      ]
    },
    marketingPoster: {
      eyebrow: isZh ? "素材编排中心" : "Creative Scheduling Hub",
      title: isZh ? "素材排期" : "Creative Scheduling",
      description: isZh ? "把首页横幅、导航入口和活动素材当成一个排期问题来管理，而不是分散在不同角落。" : "Treat banners, navigation entries, and campaign creatives as one scheduling problem instead of scattered tasks.",
      metrics: [
        { title: isZh ? "可用素材位" : "Creative Slots", value: bannerCount + navCount, detail: isZh ? "轮播与导航组合位" : "Combined banner and navigation placements", tone: "blue" },
        { title: isZh ? "当前主推" : "Current Focus", value: Math.max(1, Math.round(orderCount * 0.3)), detail: isZh ? "建议重点承接的商品" : "SKUs worth highlighting next", tone: "violet" },
        { title: isZh ? "改版频率" : "Refresh Rhythm", value: isZh ? "每周" : "Weekly", detail: isZh ? "建议保持固定节奏" : "Recommended update cadence", tone: "emerald" },
        { title: isZh ? "协同对象" : "Collaborators", value: isZh ? "运营 / 设计" : "Ops / Design", detail: isZh ? "当前主要协同角色" : "Primary collaborators", tone: "amber" }
      ],
      bars: [
        { label: isZh ? "首页横幅" : "Homepage banners", value: bannerCount },
        { label: isZh ? "导航位" : "Navigation slots", value: navCount },
        { label: isZh ? "主推货盘" : "Feature pool", value: Math.max(1, Math.round(orderCount * 0.25)) },
        { label: isZh ? "备用素材" : "Backup creatives", value: Math.max(1, Math.round((bannerCount + navCount) * 0.6)) }
      ],
      timeline: [
        { title: isZh ? "先锁定本周主题" : "Lock this week's theme", detail: isZh ? "统一首页、导航和活动素材语气" : "Align homepage, navigation, and campaign tone", tone: "blue" },
        { title: isZh ? "再替换低效素材" : "Replace weak creatives", detail: isZh ? "优先更新点击弱的入口位" : "Refresh the placements with the weakest response", tone: "amber" },
        { title: isZh ? "最后回看转化" : "Close the loop with conversion", detail: isZh ? "确认素材变化有订单支撑" : "Verify creative changes are backed by real orders", tone: "emerald" }
      ]
    },
    marketingEmail: {
      eyebrow: isZh ? "邮件触达中心" : "Email Outreach Center",
      title: isZh ? "触达邮件" : "Lifecycle Email Outreach",
      description: isZh ? "用邮件承接活动、召回和售后通知，把品牌触达从单点动作变成连续节奏。" : "Use email to support promotions, reactivation, and post-order communication as a continuous rhythm.",
      metrics: [
        { title: isZh ? "可触达客户" : "Reachable Contacts", value: customerCount, detail: isZh ? "当前后台客户资产" : "Current customer audience", tone: "blue" },
        { title: isZh ? "推荐批次" : "Suggested Batches", value: 3, detail: isZh ? "拉新 / 召回 / 通知" : "Acquire / reactivate / notify", tone: "violet" },
        { title: isZh ? "邮件入口" : "Mail-linked Campaigns", value: Math.max(1, bannerCount), detail: isZh ? "可与活动联动的素材位" : "Placements that can pair with email", tone: "emerald" },
        { title: isZh ? "优先目标" : "Priority Goal", value: isZh ? "复购" : "Repeat Buy", detail: isZh ? "当前建议聚焦目标" : "Suggested near-term objective", tone: "amber" }
      ],
      bars: [
        { label: isZh ? "活动通知" : "Campaign notices", value: Math.max(1, Math.round(customerCount * 0.35)) },
        { label: isZh ? "召回邮件" : "Reactivation mail", value: Math.max(1, Math.round(customerCount * 0.18)) },
        { label: isZh ? "订单通知" : "Order updates", value: Math.max(1, Math.round(orderCount * 0.7)) },
        { label: isZh ? "售后关怀" : "After-sales follow-up", value: Math.max(1, Math.round(orderCount * 0.14)) }
      ],
      timeline: [
        { title: isZh ? "定义本周触达目标" : "Define the outreach goal", detail: isZh ? "先区分拉新、促活还是通知" : "Start by deciding whether the batch is acquisition, reactivation, or notice", tone: "blue" },
        { title: isZh ? "统一品牌素材" : "Align branded assets", detail: isZh ? "让邮件素材和站内入口保持一致" : "Keep email creative aligned with onsite placements", tone: "amber" },
        { title: isZh ? "复盘回流效果" : "Review return traffic", detail: isZh ? "看邮件是否带回订单和询盘" : "Check whether email drives orders and inquiries back", tone: "emerald" }
      ]
    },
    discounts: {
      eyebrow: isZh ? "促销策略中心" : "Promotion Strategy Hub",
      title: isZh ? "优惠券与折扣" : "Coupons and Discounts",
      description: isZh ? "把折扣策略从简单配置页升级为可读的经营面板，便于运营快速判断力度与覆盖面。" : "Turn discounts into a readable business dashboard so operations can assess depth and reach quickly.",
      metrics: [
        { title: isZh ? "活动订单" : "Promo Orders", value: Math.max(1, Math.round(orderCount * 0.36)), detail: isZh ? "估算参与促销的订单" : "Estimated orders touched by promotions", tone: "blue" },
        { title: isZh ? "让利区间" : "Discount Band", value: "8%-18%", detail: isZh ? "建议保持在可控利润带内" : "Recommended margin-safe range", tone: "violet" },
        { title: isZh ? "潜在复购" : "Repeat Opportunity", value: Math.max(1, Math.round(customerCount * 0.18)), detail: isZh ? "适合券后召回的客户" : "Customers fit for coupon reactivation", tone: "emerald" },
        { title: isZh ? "已投放入口" : "Active Placements", value: bannerCount, detail: isZh ? "当前可承接优惠动作的素材入口" : "Current placements able to support offers", tone: "amber" }
      ],
      bars: [
        { label: isZh ? "新人券" : "Welcome coupon", value: Math.max(1, Math.round(customerCount * 0.15)) },
        { label: isZh ? "满减券" : "Threshold coupon", value: Math.max(1, Math.round(orderCount * 0.22)) },
        { label: isZh ? "复购券" : "Repeat coupon", value: Math.max(1, Math.round(customerCount * 0.12)) },
        { label: isZh ? "组合折扣" : "Bundle discount", value: Math.max(1, Math.round(orderCount * 0.18)) }
      ],
      timeline: [
        { title: isZh ? "检查券门槛" : "Review coupon threshold", detail: isZh ? "避免门槛过高导致领取后不转化" : "Prevent redemption drops from overly high thresholds", tone: "blue" },
        { title: isZh ? "清理过期券" : "Retire expired coupons", detail: isZh ? "统一入口文案，避免用户看到失效信息" : "Align entry copy and remove stale incentives", tone: "amber" },
        { title: isZh ? "追加召回批次" : "Launch reactivation batch", detail: isZh ? "针对沉默用户做低频再唤醒" : "Re-engage silent users with a limited-touch batch", tone: "emerald" }
      ]
    },
    analytics: {
      eyebrow: isZh ? "经营数据中心" : "Business Analytics Center",
      title: isZh ? "数据分析" : "Analytics",
      description: isZh ? "把访问、订单、客户三类核心指标压缩到同一页，减少在多个旧模块之间切换。" : "Bring traffic, orders, and customer signals into one page so teams stop bouncing between old modules.",
      metrics: [
        { title: isZh ? "访问入口" : "Traffic Entrances", value: navCount + bannerCount, detail: isZh ? "可直接引流的前台入口" : "Storefront entry points driving visits", tone: "blue" },
        { title: isZh ? "订单表现" : "Order Throughput", value: orderCount, detail: isZh ? "当前统计订单量" : "Orders currently tracked", tone: "violet" },
        { title: isZh ? "客户资产" : "Customer Base", value: customerCount, detail: isZh ? "当前用户规模" : "Current user footprint", tone: "emerald" },
        { title: isZh ? "当前搜索" : "Active Search", value: adminQuery || (isZh ? "未使用" : "Idle"), detail: isZh ? "结合当前筛选上下文" : "Reflects the current admin search context", tone: "amber" }
      ],
      bars: [
        { label: isZh ? "轮播流量" : "Banner traffic", value: Math.max(1, bannerCount * 3) },
        { label: isZh ? "导航流量" : "Navigation traffic", value: Math.max(1, navCount * 2) },
        { label: isZh ? "订单转化" : "Order conversion", value: Math.max(1, Math.round(orderCount * 0.55)) },
        { label: isZh ? "客户沉淀" : "Customer retention", value: Math.max(1, Math.round(customerCount * 0.42)) }
      ],
      timeline: [
        { title: isZh ? "先看访问入口质量" : "Start with entry quality", detail: isZh ? "判断哪些入口值得保留首页资源位" : "Decide which entry points deserve homepage real estate", tone: "blue" },
        { title: isZh ? "再看订单兑现" : "Then check order payoff", detail: isZh ? "确认流量不是只停留在曝光层" : "Ensure traffic is turning into real orders", tone: "amber" },
        { title: isZh ? "最后看客户沉淀" : "Close with customer retention", detail: isZh ? "识别是否真正带来可复用用户资产" : "See whether growth creates reusable customer value", tone: "emerald" }
      ]
    },
    analyticsSales: {
      eyebrow: isZh ? "销售分析中心" : "Sales Analytics Center",
      title: isZh ? "销售分析" : "Sales Analytics",
      description: isZh ? "把订单规模、营收和履约兑现放进同一张经营视图。" : "Bring order volume, revenue, and fulfillment payoff into one sales view.",
      metrics: [
        { title: isZh ? "订单量" : "Order Count", value: orderCount, detail: isZh ? "当前统计期订单量" : "Orders in the current horizon", tone: "blue" },
        { title: isZh ? "营收" : "Revenue", value: `US $${totalRevenue.toFixed(2)}`, detail: isZh ? "当前统计口径营收" : "Revenue in the current window", tone: "emerald" },
        { title: isZh ? "客单价" : "Average Order", value: orderCount ? `US $${(totalRevenue / orderCount).toFixed(2)}` : "US $0.00", detail: isZh ? "订单均值" : "Average order value", tone: "violet" },
        { title: isZh ? "客户覆盖" : "Customer Reach", value: customerCount, detail: isZh ? "当前可成交客户池" : "Reachable customer base", tone: "amber" }
      ],
      bars: [
        { label: isZh ? "已完成订单" : "Delivered orders", value: Math.max(1, Math.round(orderCount * 0.4)) },
        { label: isZh ? "运输中订单" : "In-transit orders", value: Math.max(1, Math.round(orderCount * 0.24)) },
        { label: isZh ? "待履约订单" : "Pending fulfillment", value: Math.max(1, Math.round(orderCount * 0.22)) },
        { label: isZh ? "取消订单" : "Cancelled orders", value: Math.max(1, Math.round(orderCount * 0.08)) }
      ],
      timeline: [
        { title: isZh ? "先看收入分布" : "Start with revenue mix", detail: isZh ? "判断当前订单是否集中在少数成交上" : "Check whether revenue is concentrated in a few wins", tone: "blue" },
        { title: isZh ? "再看履约兑现" : "Then inspect fulfillment payoff", detail: isZh ? "确认成交没有卡在交付环节" : "Ensure wins are not stuck before delivery", tone: "amber" },
        { title: isZh ? "最后追踪客户贡献" : "Close with customer contribution", detail: isZh ? "识别哪些客户值得持续经营" : "See which customer groups are worth compounding", tone: "emerald" }
      ]
    },
    analyticsProducts: {
      eyebrow: isZh ? "商品分析中心" : "Product Analytics Center",
      title: isZh ? "商品分析" : "Product Analytics",
      description: isZh ? "用经营视角看商品表现、素材位和成交支撑，而不是只看单个 SKU。" : "Read product performance through a business lens instead of isolated SKU metrics.",
      metrics: [
        { title: isZh ? "商品入口" : "Product Entrances", value: bannerCount + navCount, detail: isZh ? "站内可承接商品曝光的入口" : "Storefront placements driving product exposure", tone: "blue" },
        { title: isZh ? "成交支撑" : "Order Support", value: orderCount, detail: isZh ? "订单对商品表现的反馈" : "Order feedback on product performance", tone: "violet" },
        { title: isZh ? "客户反馈面" : "Audience Signal", value: customerCount, detail: isZh ? "客户对商品表现的感应面" : "Customer base reacting to product mix", tone: "emerald" },
        { title: isZh ? "当前搜索" : "Current Search", value: adminQuery || (isZh ? "未使用" : "Idle"), detail: isZh ? "可结合后台筛选上下文" : "Can inherit the current admin query context", tone: "amber" }
      ],
      bars: [
        { label: isZh ? "首页曝光" : "Homepage exposure", value: Math.max(1, bannerCount * 3) },
        { label: isZh ? "导航承接" : "Navigation pickup", value: Math.max(1, navCount * 2) },
        { label: isZh ? "订单验证" : "Order validation", value: Math.max(1, Math.round(orderCount * 0.45)) },
        { label: isZh ? "客户兴趣" : "Customer interest", value: Math.max(1, Math.round(customerCount * 0.3)) }
      ],
      timeline: [
        { title: isZh ? "先看曝光位" : "Start with placements", detail: isZh ? "判断哪些商品获得了更多资源" : "Check which products are receiving more surface area", tone: "blue" },
        { title: isZh ? "再看成交反馈" : "Then inspect order feedback", detail: isZh ? "确认曝光有没有带来真实下单" : "Make sure exposure produces real orders", tone: "amber" },
        { title: isZh ? "最后看产品池调整" : "Close with portfolio action", detail: isZh ? "给下一轮主推与淘汰提供依据" : "Use the result to choose the next featured pool", tone: "emerald" }
      ]
    },
    analyticsCustomers: {
      eyebrow: isZh ? "客户分析中心" : "Customer Analytics Center",
      title: isZh ? "客户分析" : "Customer Analytics",
      description: isZh ? "把客户体量、订单转化和活动触达放在一页里看，便于判断用户资产质量。" : "Read customer scale, conversion, and campaign reach together to assess audience quality.",
      metrics: [
        { title: isZh ? "客户总量" : "Customer Base", value: customerCount, detail: isZh ? "当前用户规模" : "Current user footprint", tone: "blue" },
        { title: isZh ? "订单回应" : "Order Response", value: orderCount, detail: isZh ? "客户资产对应订单量" : "Orders tied to the current audience", tone: "violet" },
        { title: isZh ? "活动入口" : "Campaign Entrances", value: bannerCount + navCount, detail: isZh ? "可触达用户的前台入口" : "Entry points able to reach users", tone: "emerald" },
        { title: isZh ? "人均营收" : "Revenue per Customer", value: customerCount ? `US $${(totalRevenue / customerCount).toFixed(2)}` : "US $0.00", detail: isZh ? "粗看用户资产质量" : "Quick view of customer value", tone: "amber" }
      ],
      bars: [
        { label: isZh ? "可触达客户" : "Reachable users", value: customerCount },
        { label: isZh ? "转化客户" : "Converted users", value: Math.max(1, Math.round(orderCount * 0.7)) },
        { label: isZh ? "复购机会" : "Repeat potential", value: Math.max(1, Math.round(customerCount * 0.2)) },
        { label: isZh ? "沉默用户" : "Dormant users", value: Math.max(1, Math.round(customerCount * 0.14)) }
      ],
      timeline: [
        { title: isZh ? "先看客户规模" : "Start with scale", detail: isZh ? "确认当前资产池是否足够支撑动作" : "Confirm the audience pool can support campaigns", tone: "blue" },
        { title: isZh ? "再看转化密度" : "Then inspect conversion density", detail: isZh ? "判断是不是只有流量没有成交" : "See whether the audience is converting or just browsing", tone: "amber" },
        { title: isZh ? "最后看复购机会" : "Close with repeat potential", detail: isZh ? "识别值得持续经营的人群" : "Identify the groups worth compounding", tone: "emerald" }
      ]
    },
    finance: {
      eyebrow: isZh ? "财务概览中心" : "Finance Overview Center",
      title: isZh ? "财务中心" : "Finance Center",
      description: isZh ? "围绕营收、订单兑现和运营动作建立财务视角，方便老板快速判断投入产出。" : "Frame revenue, fulfillment, and operations in a finance-oriented view for faster ROI decisions.",
      metrics: [
        { title: isZh ? "累计营收" : "Total Revenue", value: money(recognizedRevenue), detail: isZh ? "来自订单与财务录入" : "Blended from orders and finance entries", tone: "emerald" },
        { title: isZh ? "订单均值" : "Average Order", value: orderCount ? `US $${(recognizedRevenue / orderCount).toFixed(2)}` : money(recognizedRevenue), detail: isZh ? "订单客单价估算" : "Estimated order value", tone: "blue" },
        { title: isZh ? "客户价值" : "Revenue per Customer", value: customerCount ? `US $${(recognizedRevenue / customerCount).toFixed(2)}` : "US $0.00", detail: isZh ? "客户价值粗看" : "Quick customer value read", tone: "violet" },
        { title: isZh ? "待审批金额" : "Pending Approvals", value: money(openApprovalAmount), detail: isZh ? "来自审批工作台" : "Live approval queue amount", tone: "amber" }
      ],
      bars: [
        { label: isZh ? "已确认收入" : "Recognized revenue", value: Math.round(recognizedRevenue) },
        { label: isZh ? "已录入支出" : "Recorded expense", value: Math.round(estimatedExpenses) },
        { label: isZh ? "待审批金额" : "Pending approvals", value: Math.round(openApprovalAmount) },
        { label: isZh ? "应收机会" : "Receivable opportunity", value: Math.round(recognizedRevenue * 0.1) }
      ],
      timeline: [
        { title: isZh ? "盘点应收与已收" : "Review collected vs outstanding", detail: isZh ? "先确认今天最关键的回款风险" : "Prioritize the most important receivable risks", tone: "blue" },
        { title: isZh ? "对照履约状态" : "Cross-check fulfillment", detail: isZh ? "避免财务与订单进度脱节" : "Keep finance aligned with fulfillment progress", tone: "amber" },
        { title: isZh ? "追踪营销投入回报" : "Track campaign ROI", detail: isZh ? "把活动成本和成交结果放在一起看" : "Read campaign costs next to conversion results", tone: "emerald" }
      ],
      records: { title: isZh ? "ERP 财务功能蓝图" : "ERP finance function blueprint", description: isZh ? "财务中心按收款、付款、票税、预算审计和报表闭环设计。" : "Finance center is designed around collection, payment, invoice-tax, budget-audit, and reporting loops.", columns: billColumns.modules, rows: financeModuleRows }
    },
    financeIncome: {
      eyebrow: isZh ? "入账记录" : "Income Records",
      title: isZh ? "入账流水" : "Income Ledger",
      description: isZh ? "把订单回款、样品费和已确认收入收拢为可追踪的入账流水。" : "Bring order collections, sample fees, and recognized revenue into a traceable income ledger.",
      metrics: [
        { title: isZh ? "已确认入账" : "Recognized Income", value: money(recognizedRevenue), detail: isZh ? "来自订单与经营数据" : "From orders and operating data", tone: "emerald" },
        { title: isZh ? "流水记录" : "Ledger Rows", value: incomeRows.length || fallbackIncomeRows.length, detail: isZh ? "当前可查看记录" : "Visible records", tone: "blue" },
        { title: isZh ? "待核销" : "Pending Check", value: money(customIncomeRows.filter((item) => item.statusKey === "pending").reduce((sum, item) => sum + item.amount, 0) || Math.round(recognizedRevenue * 0.06)), detail: isZh ? "样品费/预收款估算" : "Sample fee / prepayment estimate", tone: "amber" },
        { title: isZh ? "平均入账" : "Average Receipt", value: money(orderCount ? recognizedRevenue / orderCount : recognizedRevenue), detail: isZh ? "按订单估算" : "Estimated by order", tone: "violet" }
      ],
      bars: [
        { label: isZh ? "订单回款" : "Order collections", value: Math.round(recognizedRevenue * 0.82) },
        { label: isZh ? "样品费" : "Sample fees", value: Math.round(recognizedRevenue * 0.06) },
        { label: isZh ? "预收款" : "Prepayments", value: Math.round(recognizedRevenue * 0.08) },
        { label: isZh ? "其他收入" : "Other income", value: Math.round(recognizedRevenue * 0.04) }
      ],
      timeline: [
        { title: isZh ? "先核对订单收款" : "Match order collections first", detail: isZh ? "确认订单号、金额和状态一致" : "Match order ID, amount, and state", tone: "blue" },
        { title: isZh ? "再处理预收与样品费" : "Then clear prepayments and sample fees", detail: isZh ? "避免收入挂账时间过长" : "Avoid long-running uncleared income", tone: "amber" },
        { title: isZh ? "最后沉淀流水" : "Finally archive the ledger", detail: isZh ? "形成可复盘的收入记录" : "Keep records auditable", tone: "emerald" }
      ],
      records: { title: isZh ? "入账流水记录" : "Income ledger records", description: isZh ? "用于查看已确认和待核销的入账来源。" : "Review recognized and pending income sources.", columns: incomeRecordColumns, rows: incomeRows.length ? incomeRows : fallbackIncomeRows }
    },
    financeExpenses: {
      eyebrow: isZh ? "公司支出" : "Company Expenses",
      title: isZh ? "支出账单" : "Expense Bills",
      description: isZh ? "按采购、物流、营销和行政分类记录公司开销，便于后续审批与利润核算。" : "Record purchasing, logistics, marketing, and office spending for approval and margin review.",
      metrics: [
        { title: isZh ? "本月支出" : "Monthly Spend", value: money(estimatedExpenses), detail: isZh ? "按经营数据估算" : "Estimated from operating data", tone: "amber" },
        { title: isZh ? "采购占比" : "Purchasing Share", value: "57%", detail: isZh ? "当前主要成本项" : "Main current cost driver", tone: "blue" },
        { title: isZh ? "物流占比" : "Logistics Share", value: "18%", detail: isZh ? "发货相关费用" : "Shipment-related cost", tone: "violet" },
        { title: isZh ? "可控费用" : "Controllable Spend", value: money(Math.round(estimatedExpenses * 0.25)), detail: isZh ? "营销/行政/手续费" : "Marketing / office / fees", tone: "emerald" }
      ],
      bars: [
        { label: isZh ? "采购成本" : "Purchasing", value: Math.round(estimatedExpenses * 0.57) },
        { label: isZh ? "物流运费" : "Logistics", value: Math.round(estimatedExpenses * 0.18) },
        { label: isZh ? "营销投放" : "Marketing", value: Math.round(estimatedExpenses * 0.14) },
        { label: isZh ? "行政与手续费" : "Office and fees", value: Math.round(estimatedExpenses * 0.11) }
      ],
      timeline: [
        { title: isZh ? "采购费用先归集" : "Collect purchasing bills first", detail: isZh ? "关联采购单和供应商" : "Link purchase orders and suppliers", tone: "blue" },
        { title: isZh ? "物流费用再分摊" : "Allocate logistics costs next", detail: isZh ? "按订单或批次分摊运费" : "Allocate freight by order or batch", tone: "amber" },
        { title: isZh ? "月底统一复核" : "Review at month end", detail: isZh ? "形成月度利润口径" : "Create a monthly profit basis", tone: "emerald" }
      ],
      records: { title: isZh ? "支出账单记录" : "Expense bill records", description: isZh ? "用于登记和查看各部门开销。" : "Register and review departmental expenses.", columns: expenseRecordColumns, rows: expenseRows }
    },
    financeReceivables: {
      eyebrow: isZh ? "应收管理" : "Receivables Management",
      title: isZh ? "应收账款" : "Receivables",
      description: isZh ? "按客户、订单、到期日和账龄管理未收款项，帮助财务优先催收。" : "Manage open balances by customer, order, due date, and aging so finance can prioritize collections.",
      metrics: [
        { title: isZh ? "应收余额" : "Open AR", value: money(Math.round(recognizedRevenue * 0.18)), detail: isZh ? "待收款项估算" : "Estimated open receivables", tone: "amber" },
        { title: isZh ? "超 30 天" : "> 30 Days", value: money(Math.round(recognizedRevenue * 0.06)), detail: isZh ? "需优先跟进" : "Needs priority follow-up", tone: "violet" },
        { title: isZh ? "待匹配到账" : "Unmatched Receipts", value: money(Math.round(recognizedRevenue * 0.04)), detail: isZh ? "银行流水待核对" : "Bank receipts to match", tone: "blue" },
        { title: isZh ? "催收动作" : "Collection Actions", value: receivableRows.length, detail: isZh ? "当前催收清单" : "Current action list", tone: "emerald" }
      ],
      bars: receivableRows.map((item) => ({ label: item.aging, value: Number(String(item.amount).replace(/[^0-9.]/g, "")) || 0 })),
      timeline: [
        { title: isZh ? "先看账龄" : "Start with aging", detail: isZh ? "优先处理超过 30 天的款项" : "Prioritize balances over 30 days", tone: "blue" },
        { title: isZh ? "再核对到账" : "Then match receipts", detail: isZh ? "把银行流水与订单应收匹配" : "Match bank receipts to order AR", tone: "amber" },
        { title: isZh ? "最后更新催收结果" : "Update collection results", detail: isZh ? "保留催收动作和客户反馈" : "Keep collection actions and customer feedback", tone: "emerald" }
      ],
      records: { title: isZh ? "应收账款清单" : "Receivables aging list", description: isZh ? "用于跟踪客户、账龄、到期日和催收动作。" : "Track customer, aging, due date, and collection action.", columns: receivableRecordColumns, rows: receivableRows }
    },
    financePayables: {
      eyebrow: isZh ? "应付管理" : "Payables Management",
      title: isZh ? "应付账款" : "Payables",
      description: isZh ? "把采购货款、物流费用和平台服务费纳入付款计划，避免漏付和超付。" : "Put purchase, logistics, and platform fees into a payment plan to avoid missing or overpaying bills.",
      metrics: [
        { title: isZh ? "应付余额" : "Open AP", value: money(Math.round(estimatedExpenses * 0.83)), detail: isZh ? "待付款项估算" : "Estimated open payables", tone: "amber" },
        { title: isZh ? "本周到期" : "Due This Week", value: money(Math.round(estimatedExpenses * 0.45)), detail: isZh ? "需安排付款" : "Needs payment scheduling", tone: "blue" },
        { title: isZh ? "待审批" : "Pending Approval", value: customExpenseRows.filter((item) => ["draft", "submitted"].includes(item.statusKey)).length || payableRows.filter((item) => /审批|Approval/i.test(item.status)).length, detail: isZh ? "付款审批条目" : "Payment approval rows", tone: "violet" },
        { title: isZh ? "供应商条目" : "Vendor Rows", value: payableRows.length, detail: isZh ? "当前供应商账单" : "Current vendor bills", tone: "emerald" }
      ],
      bars: payableRows.map((item) => ({ label: item.category, value: Number(String(item.amount).replace(/[^0-9.]/g, "")) || 0 })),
      timeline: [
        { title: isZh ? "按到期日排款" : "Schedule by due date", detail: isZh ? "先保证本周到期款项" : "Start with bills due this week", tone: "blue" },
        { title: isZh ? "审批后付款" : "Pay after approval", detail: isZh ? "控制供应商付款风险" : "Control vendor payment risk", tone: "amber" },
        { title: isZh ? "付款后回写" : "Write back after payment", detail: isZh ? "保留付款凭证和对账状态" : "Keep payment proof and reconciliation state", tone: "emerald" }
      ],
      records: { title: isZh ? "应付账款清单" : "Payables list", description: isZh ? "用于查看供应商、到期日和付款状态。" : "Review vendors, due dates, and payment status.", columns: payableRecordColumns, rows: payableRows }
    },
    financeInvoices: {
      eyebrow: isZh ? "票据中心" : "Invoice Center",
      title: isZh ? "发票管理" : "Invoice Management",
      description: isZh ? "统一管理销项发票、进项发票和费用票据，确保收支与税务口径一致。" : "Manage sales invoices, input invoices, and expense receipts so cash and tax basis stay aligned.",
      metrics: [
        { title: isZh ? "待开票" : "Pending Issue", value: money(Math.round(recognizedRevenue * 0.22)), detail: isZh ? "订单收入待开票" : "Order revenue pending invoice", tone: "amber" },
        { title: isZh ? "待收票" : "Awaiting Input", value: money(Math.round(estimatedExpenses * 0.38)), detail: isZh ? "采购成本待收票" : "Purchase invoices to receive", tone: "blue" },
        { title: isZh ? "票据条目" : "Invoice Rows", value: invoiceRows.length, detail: isZh ? "当前票据队列" : "Current invoice queue", tone: "violet" },
        { title: isZh ? "待归档" : "To Archive", value: invoiceRows.filter((item) => /归档|Archive/i.test(item.status)).length, detail: isZh ? "费用票据归档" : "Receipt archiving", tone: "emerald" }
      ],
      bars: invoiceRows.map((item) => ({ label: item.type, value: Number(String(item.amount).replace(/[^0-9.]/g, "")) || 0 })),
      timeline: [
        { title: isZh ? "先开销项" : "Issue sales invoices", detail: isZh ? "对已收款订单补齐开票" : "Complete invoicing for collected orders", tone: "blue" },
        { title: isZh ? "再催进项" : "Collect input invoices", detail: isZh ? "跟供应商索取采购和物流票据" : "Request purchase and logistics invoices", tone: "amber" },
        { title: isZh ? "最后归档" : "Archive receipts", detail: isZh ? "形成后续税务复核依据" : "Create tax review evidence", tone: "emerald" }
      ],
      records: { title: isZh ? "发票与票据队列" : "Invoice and receipt queue", description: isZh ? "用于跟进发票类型、关联业务和状态。" : "Track invoice type, related business, and status.", columns: invoiceRecordColumns, rows: invoiceRows }
    },
    financeTax: {
      eyebrow: isZh ? "税务复核" : "Tax Review",
      title: isZh ? "税务管理" : "Tax Management",
      description: isZh ? "把销项、进项和所得税预留放在同一视图，支持月度与季度申报准备。" : "Read output tax, input tax, and income-tax reserves together for monthly and quarterly filing readiness.",
      metrics: [
        { title: isZh ? "销项税" : "Output Tax", value: taxRows[0]?.amount || money(0), detail: isZh ? "按收入估算" : "Estimated from revenue", tone: "amber" },
        { title: isZh ? "进项抵扣" : "Input Credit", value: taxRows[1]?.amount || money(0), detail: isZh ? "按费用票据估算" : "Estimated from expense invoices", tone: "blue" },
        { title: isZh ? "所得税预留" : "Income Tax Reserve", value: taxRows[2]?.amount || money(0), detail: isZh ? "按利润预估" : "Estimated from profit", tone: "violet" },
        { title: isZh ? "税项条目" : "Tax Rows", value: taxRows.length, detail: isZh ? "当前复核清单" : "Current review list", tone: "emerald" }
      ],
      bars: taxRows.map((item) => ({ label: item.taxItem, value: Number(String(item.amount).replace(/[^0-9.]/g, "")) || 0 })),
      timeline: [
        { title: isZh ? "先补票据" : "Complete invoice evidence", detail: isZh ? "保证进销项数据有凭证" : "Ensure output and input amounts have evidence", tone: "blue" },
        { title: isZh ? "再算税额" : "Calculate tax", detail: isZh ? "按当前税率和利润口径预估" : "Estimate by current rates and profit basis", tone: "amber" },
        { title: isZh ? "最后准备申报" : "Prepare filing", detail: isZh ? "沉淀申报口径和复核记录" : "Archive filing basis and review notes", tone: "emerald" }
      ],
      records: { title: isZh ? "税务复核表" : "Tax review table", description: isZh ? "用于查看税项、基数、税率和处理节点。" : "Review tax item, base, rate, and milestone.", columns: taxRecordColumns, rows: taxRows }
    },
    financeBudget: {
      eyebrow: isZh ? "预算控制" : "Budget Control",
      title: isZh ? "预算费用" : "Budget & Expenses",
      description: isZh ? "按部门管理预算、已用金额和偏差，提前发现费用超支。" : "Manage budget, used amount, and variance by department to catch overspend early.",
      metrics: [
        { title: isZh ? "总预算" : "Total Budget", value: money(budgetRows.reduce((sum, item) => sum + (Number(String(item.budget).replace(/[^0-9.]/g, "")) || 0), 0)), detail: isZh ? "当前部门预算" : "Current department budgets", tone: "blue" },
        { title: isZh ? "已占用" : "Used", value: money(budgetRows.reduce((sum, item) => sum + (Number(String(item.used).replace(/[^0-9.]/g, "")) || 0), 0)), detail: isZh ? "已发生费用" : "Recognized spend", tone: "amber" },
        { title: isZh ? "超支预警" : "Overspend Alerts", value: budgetRows.filter((item) => /^\+/.test(item.variance)).length, detail: isZh ? "需要部门说明" : "Needs department note", tone: "violet" },
        { title: isZh ? "预算部门" : "Budget Departments", value: budgetRows.length, detail: isZh ? "纳入预算控制" : "In budget scope", tone: "emerald" }
      ],
      bars: budgetRows.map((item) => ({ label: item.department, value: Number(String(item.used).replace(/[^0-9.]/g, "")) || 0 })),
      timeline: [
        { title: isZh ? "月初确认预算" : "Confirm budget at month start", detail: isZh ? "锁定各部门费用边界" : "Lock department spend boundaries", tone: "blue" },
        { title: isZh ? "月中看偏差" : "Review variance mid-month", detail: isZh ? "对超支项目要求说明" : "Ask for notes on overspend", tone: "amber" },
        { title: isZh ? "月底沉淀复盘" : "Archive review at month end", detail: isZh ? "作为下一月预算依据" : "Use as next month's budget basis", tone: "emerald" }
      ],
      records: { title: isZh ? "预算执行表" : "Budget execution table", description: isZh ? "按部门查看预算、已用、偏差和状态。" : "Review budget, used amount, variance, and status by department.", columns: budgetRecordColumns, rows: budgetRows }
    },
    financeDaily: {
      eyebrow: isZh ? "每日账单" : "Daily Bills",
      title: isZh ? "日账单" : "Daily Bills",
      description: isZh ? "每天汇总入账、开销和净现金，快速识别异常波动。" : "Summarize income, expenses, and net cash daily to spot abnormal movement.",
      metrics: [
        { title: isZh ? "今日入账" : "Today's Income", value: dailyBillRows[0]?.income || money(0), detail: isZh ? "当天入账估算" : "Estimated daily income", tone: "emerald" },
        { title: isZh ? "今日开销" : "Today's Expense", value: dailyBillRows[0]?.expense || money(0), detail: isZh ? "当天支出估算" : "Estimated daily spend", tone: "amber" },
        { title: isZh ? "今日净额" : "Today's Net", value: dailyBillRows[0]?.net || money(0), detail: isZh ? "入账减开销" : "Income minus expense", tone: "blue" },
        { title: isZh ? "近7日记录" : "7-Day Rows", value: dailyBillRows.length, detail: isZh ? "日账单条目" : "Daily bill rows", tone: "violet" }
      ],
      bars: dailyBillRows.slice(0, 4).map((item) => ({ label: item.date, value: Number(String(item.net).replace(/[^0-9.]/g, "")) || 0 })),
      timeline: [
        { title: isZh ? "上午核对昨日流水" : "Review yesterday's ledger in the morning", detail: isZh ? "确认入账和支出没有漏项" : "Confirm income and expenses are complete", tone: "blue" },
        { title: isZh ? "下午处理异常" : "Handle exceptions in the afternoon", detail: isZh ? "优先处理金额异常和未匹配项" : "Prioritize amount exceptions and unmatched rows", tone: "amber" },
        { title: isZh ? "收盘生成日账单" : "Close with a daily bill", detail: isZh ? "沉淀每日收支净额" : "Archive daily net cash", tone: "emerald" }
      ],
      records: { title: isZh ? "近 7 日日账单" : "Recent 7-day bills", description: isZh ? "按日查看入账、开销和净额。" : "Review income, expense, and net cash by day.", columns: billColumns.daily, rows: dailyBillRows }
    },
    financeMonthly: {
      eyebrow: isZh ? "月度结账" : "Monthly Closing",
      title: isZh ? "月度结账" : "Monthly Closing",
      description: isZh ? "按月汇总入账、开销、利润和预算偏差，为老板看经营趋势提供口径。" : "Summarize income, expense, profit, and budget variance monthly for management trend review.",
      metrics: [
        { title: isZh ? "本月入账" : "Monthly Income", value: monthlyBillRows[0]?.income || money(0), detail: isZh ? "本月收入口径" : "This month's income basis", tone: "emerald" },
        { title: isZh ? "本月开销" : "Monthly Expense", value: monthlyBillRows[0]?.expense || money(0), detail: isZh ? "本月费用口径" : "This month's expense basis", tone: "amber" },
        { title: isZh ? "本月利润" : "Monthly Profit", value: monthlyBillRows[0]?.profit || money(0), detail: isZh ? "入账减开销" : "Income minus expense", tone: "blue" },
        { title: isZh ? "月度记录" : "Monthly Rows", value: monthlyBillRows.length, detail: isZh ? "可复盘月份" : "Reviewable months", tone: "violet" }
      ],
      bars: monthlyBillRows.slice(0, 4).map((item) => ({ label: item.period, value: Number(String(item.profit).replace(/[^0-9.]/g, "")) || 0 })),
      timeline: [
        { title: isZh ? "月初锁定预算" : "Lock budget at month start", detail: isZh ? "给采购、物流和营销设定费用边界" : "Set spend boundaries for procurement, logistics, and marketing", tone: "blue" },
        { title: isZh ? "月中看偏差" : "Check variance mid-month", detail: isZh ? "对照入账和开销偏差" : "Compare income and expense variance", tone: "amber" },
        { title: isZh ? "月末复盘利润" : "Review profit at month end", detail: isZh ? "形成月报和下一月预算依据" : "Create monthly report and next budget basis", tone: "emerald" }
      ],
      records: { title: isZh ? "月度结账汇总" : "Monthly closing summary", description: isZh ? "按月查看收入、费用、利润和结账口径。" : "Review income, expense, profit, and closing basis by month.", columns: billColumns.monthly, rows: monthlyBillRows }
    },
    financeYearly: {
      eyebrow: isZh ? "财务年报" : "Annual Statements",
      title: isZh ? "财务年报" : "Annual Statements",
      description: isZh ? "按年复盘营收、费用、税费和净利润，适合做经营总结。" : "Review annual revenue, expenses, tax, and net profit for business retrospectives.",
      metrics: [
        { title: isZh ? "年度营收" : "Annual Revenue", value: yearlyBillRows[0]?.income || money(0), detail: isZh ? "当前年度估算" : "Current-year estimate", tone: "emerald" },
        { title: isZh ? "年度费用" : "Annual Expense", value: yearlyBillRows[0]?.expense || money(0), detail: isZh ? "当前年度费用" : "Current-year expense", tone: "amber" },
        { title: isZh ? "税费预估" : "Tax Estimate", value: yearlyBillRows[0]?.tax || money(0), detail: isZh ? "按营收估算" : "Estimated from revenue", tone: "violet" },
        { title: isZh ? "年度净利" : "Annual Net", value: yearlyBillRows[0]?.net || money(0), detail: isZh ? "扣除费用和税费" : "After expense and tax", tone: "blue" }
      ],
      bars: yearlyBillRows.map((item) => ({ label: String(item.year), value: Number(String(item.net).replace(/[^0-9.]/g, "")) || 0 })),
      timeline: [
        { title: isZh ? "季度滚动复盘" : "Quarterly rolling review", detail: isZh ? "用季度节奏提前发现利润偏差" : "Find margin variance before year end", tone: "blue" },
        { title: isZh ? "年中校准预算" : "Calibrate budget mid-year", detail: isZh ? "根据上半年实际费用调整下半年预算" : "Adjust H2 budget from H1 actuals", tone: "amber" },
        { title: isZh ? "年底沉淀报表" : "Archive annual statements", detail: isZh ? "形成年度收入、费用、税费和利润记录" : "Archive annual revenue, expense, tax, and profit", tone: "emerald" }
      ],
      records: { title: isZh ? "年度汇总记录" : "Annual summary records", description: isZh ? "按年查看营收、费用、税费与净利润。" : "Review revenue, expense, tax, and net profit by year.", columns: billColumns.yearly, rows: yearlyBillRows }
    },
    financeReconciliation: {
      eyebrow: isZh ? "财务对账" : "Finance Reconciliation",
      title: isZh ? "收支对账" : "Reconciliation",
      description: isZh ? "把应收、已收、应付、开票和税费放在同一面板，减少账实不一致。" : "Put receivables, collections, payables, invoices, and tax in one panel to reduce reconciliation drift.",
      metrics: [
        { title: isZh ? "待催收" : "Receivable Follow-up", value: money(Math.round(recognizedRevenue * 0.18)), detail: isZh ? "应收账款估算" : "Estimated receivables", tone: "amber" },
        { title: isZh ? "已匹配" : "Matched", value: money(Math.round(recognizedRevenue * 0.82)), detail: isZh ? "已收款匹配" : "Matched collections", tone: "emerald" },
        { title: isZh ? "待审批付款" : "Payables Approval", value: money(Math.round(estimatedExpenses * 0.45)), detail: isZh ? "应付款待处理" : "Payables pending", tone: "blue" },
        { title: isZh ? "待开票" : "Pending Invoices", value: money(Math.round(recognizedRevenue * 0.22)), detail: isZh ? "开票队列" : "Invoice queue", tone: "violet" }
      ],
      bars: reconciliationRows.map((item) => ({ label: item.subject, value: Number(String(item.amount).replace(/[^0-9.]/g, "")) || 0 })),
      timeline: [
        { title: isZh ? "先对订单与收款" : "Match orders and collections first", detail: isZh ? "优先处理订单金额和实际到账差异" : "Prioritize differences between order amount and receipts", tone: "blue" },
        { title: isZh ? "再对发票与付款" : "Then match invoices and payments", detail: isZh ? "确认待开票和应付款没有遗漏" : "Confirm invoice and payable queues are complete", tone: "amber" },
        { title: isZh ? "最后复核税费" : "Review tax last", detail: isZh ? "把税费预估纳入利润复盘" : "Include tax estimates in profit review", tone: "emerald" }
      ],
      records: { title: isZh ? "对账清单" : "Reconciliation checklist", description: isZh ? "用于核对收支、开票与税费状态。" : "Use it to reconcile cash, invoices, and tax state.", columns: reconciliationRecordColumns, rows: reconciliationRows }
    },
    financeApprovals: {
      eyebrow: isZh ? "审批与审计" : "Approval and Audit",
      title: isZh ? "审批审计" : "Approvals & Audit",
      description: isZh ? "把付款、退款、费用异常和审计留痕集中到同一页，保证财务动作可追溯。" : "Centralize payment, refund, expense exceptions, and audit trail so finance actions are traceable.",
      metrics: [
        { title: isZh ? "待审批金额" : "Pending Amount", value: money(openApprovalAmount || approvalRows.reduce((sum, item) => sum + (Number(String(item.amount).replace(/[^0-9.]/g, "")) || 0), 0)), detail: isZh ? "审批队列金额" : "Amount in approval queue", tone: "amber" },
        { title: isZh ? "审批条目" : "Approval Rows", value: (approvalRowsLive || approvalRows).length, detail: isZh ? "当前待处理事项" : "Current pending items", tone: "blue" },
        { title: isZh ? "退款复核" : "Refund Review", value: money(Math.round(recognizedRevenue * 0.03)), detail: isZh ? "取消订单退款" : "Cancelled-order refunds", tone: "violet" },
        { title: isZh ? "审计风险" : "Audit Risks", value: customApprovalRows.filter((item) => item.statusKey === "reviewing").length || approvalRows.filter((item) => /说明|Need note/i.test(item.status)).length, detail: isZh ? "需补充说明" : "Needs explanation", tone: "emerald" }
      ],
      bars: (approvalRowsLive || approvalRows).map((item) => ({ label: item.type, value: Number(String(item.amount).replace(/[^0-9.]/g, "")) || 0 })),
      timeline: [
        { title: isZh ? "先看金额和权限" : "Start with amount and authority", detail: isZh ? "大额付款需经理复核" : "Large payments need manager review", tone: "blue" },
        { title: isZh ? "再核对凭证" : "Then check evidence", detail: isZh ? "确认订单、合同、发票一致" : "Match order, contract, and invoice", tone: "amber" },
        { title: isZh ? "最后记录审计说明" : "Record audit notes", detail: isZh ? "保留每次审批原因和处理人" : "Keep reasons and actors for every approval", tone: "emerald" }
      ],
      records: { title: isZh ? "审批审计清单" : "Approval audit list", description: isZh ? "用于查看审批类型、事项、金额、负责人和状态。" : "Review approval type, subject, amount, owner, and status.", columns: approvalRecordColumns, rows: approvalRowsLive || approvalRows }
    },
    financeReports: {
      eyebrow: isZh ? "财务报表" : "Finance Reports",
      title: isZh ? "报表导出" : "Reports & Export",
      description: isZh ? "把日报、月报、年报和经营指标沉淀为可导出的报表任务。" : "Turn daily, monthly, annual, and operating metrics into exportable report jobs.",
      metrics: [
        { title: isZh ? "可导出报表" : "Exportable Reports", value: reportRows.length, detail: isZh ? "当前报表任务" : "Current report jobs", tone: "blue" },
        { title: isZh ? "现金流日报" : "Cashflow Daily", value: isZh ? "就绪" : "Ready", detail: isZh ? "支持 CSV / XLSX" : "CSV / XLSX available", tone: "emerald" },
        { title: isZh ? "月度利润表" : "Monthly P&L", value: isZh ? "结账中" : "Closing", detail: isZh ? "等待月末锁账" : "Waiting for close", tone: "amber" },
        { title: isZh ? "年度报表" : "Annual Report", value: String(today.getFullYear()), detail: isZh ? "年度经营口径" : "Annual operating basis", tone: "violet" }
      ],
      bars: [
        { label: isZh ? "日报" : "Daily", value: 1 },
        { label: isZh ? "月报" : "Monthly", value: 1 },
        { label: isZh ? "年报" : "Annual", value: 1 },
        { label: isZh ? "经营指标" : "Operating metrics", value: 1 }
      ],
      timeline: [
        { title: isZh ? "日报自动沉淀" : "Archive daily reports", detail: isZh ? "每天形成现金流快照" : "Create a daily cashflow snapshot", tone: "blue" },
        { title: isZh ? "月末结账后导出" : "Export after monthly close", detail: isZh ? "利润表等月结后锁定" : "Lock P&L after monthly close", tone: "amber" },
        { title: isZh ? "年报汇总给管理层" : "Summarize annual report", detail: isZh ? "用于经营复盘和预算规划" : "Use for business review and budget planning", tone: "emerald" }
      ],
      records: { title: isZh ? "报表任务清单" : "Report job list", description: isZh ? "用于查看报表周期、负责人、状态和导出格式。" : "Review period, owner, status, and export format.", columns: reportRecordColumns, rows: reportRows }
    }
  };

  const config = tabConfigs[adminTab] || tabConfigs.analytics;

  return (
    <section className="admin-insights-page">
      <article className="admin-insights-hero">
        <div>
          <p className="admin-insights-eyebrow">{config.eyebrow}</p>
          <h3>{config.title}</h3>
          <p>{config.description}</p>
        </div>
        <div className="admin-insights-hero-chip">
          <span>{isZh ? "实时上下文" : "Live Context"}</span>
          <strong>{adminQuery || (isZh ? "后台搜索空闲" : "Admin search idle")}</strong>
        </div>
      </article>

      <section className="admin-insights-metric-grid">
        {config.metrics.map((item) => (
          <InsightMetric key={item.title} {...item} />
        ))}
      </section>

      {shouldShowFinanceWorkbench ? (
        <section className={`admin-finance-workbench ${financeWorkbenchSections.length === 1 ? "single" : ""}`}>
          <div className="admin-finance-workbench-head">
            <div>
              <h4>{isZh ? "财务录入工作台" : "Finance Entry Workspace"}</h4>
              <p>{isZh ? "先把入账、支出和审批流录进去，再让其他财务页自动复用这些数据。" : "Capture income, expense, and approval flow first, then let the other finance pages reuse the same data."}</p>
            </div>
            <div className="admin-finance-workbench-summary">
              <span>{isZh ? "入账" : "Income"}: {customIncomeRows.length}</span>
              <span>{isZh ? "支出" : "Expense"}: {customExpenseRows.length}</span>
              <span>{isZh ? "应收" : "Receivable"}: {customReceivableRows.length}</span>
              <span>{isZh ? "应付" : "Payable"}: {customPayableRows.length}</span>
              <span>{isZh ? "发票" : "Invoice"}: {customInvoiceRows.length}</span>
              <span>{isZh ? "审批" : "Approvals"}: {customApprovalRows.length}</span>
            </div>
          </div>

          {financeNotice ? <p className="admin-finance-notice">{financeNotice}</p> : null}

          <div className="admin-finance-workbench-grid">
            {financeWorkbenchSections.includes("income") ? (
              <article className="admin-finance-editor-card">
                <div className="admin-finance-editor-head">
                  <h5>{isZh ? "新增入账" : "Add Income"}</h5>
                  <span>{isZh ? "回款 / 预收 / 样品费" : "Collections / prepayments / sample fees"}</span>
                </div>
                <div className="admin-finance-editor-fields">
                  <label>
                    <span>{isZh ? "入账来源" : "Source"}</span>
                    <input type="text" value={incomeDraft.source} onChange={(event) => { setIncomeDraft((prev) => ({ ...prev, source: event.target.value })); }} placeholder={isZh ? "例如：订单回款 / 样品费" : "Order collection / sample fee"} />
                  </label>
                  <label>
                    <span>{isZh ? "日期" : "Date"}</span>
                    <input type="date" value={incomeDraft.date} onChange={(event) => { setIncomeDraft((prev) => ({ ...prev, date: event.target.value })); }} />
                  </label>
                  <label>
                    <span>{isZh ? "金额" : "Amount"}</span>
                    <input type="number" min="0" step="0.01" value={incomeDraft.amount} onChange={(event) => { setIncomeDraft((prev) => ({ ...prev, amount: event.target.value })); }} placeholder="0.00" />
                  </label>
                  <label>
                    <span>{isZh ? "状态" : "Status"}</span>
                    <select value={incomeDraft.statusKey} onChange={(event) => { setIncomeDraft((prev) => ({ ...prev, statusKey: event.target.value })); }}>
                      {Object.entries(financeStatusLabels.income).map(([key, label]) => (
                        <option key={key} value={key}>{label}</option>
                      ))}
                    </select>
                  </label>
                </div>
                <div className="admin-finance-editor-actions">
                  <button type="button" className="admin-finance-primary-action" onClick={submitIncomeDraft}>{isZh ? "加入流水" : "Add to ledger"}</button>
                </div>
              </article>
            ) : null}

            {financeWorkbenchSections.includes("expense") ? (
              <article className="admin-finance-editor-card">
                <div className="admin-finance-editor-head">
                  <h5>{isZh ? "新增支出" : "Add Expense"}</h5>
                  <span>{isZh ? "采购 / 物流 / 营销 / 行政" : "Procurement / logistics / marketing / office"}</span>
                </div>
                <div className="admin-finance-editor-fields">
                  <label>
                    <span>{isZh ? "支出项目" : "Expense Item"}</span>
                    <input type="text" value={expenseDraft.item} onChange={(event) => { setExpenseDraft((prev) => ({ ...prev, item: event.target.value })); }} placeholder={isZh ? "例如：国际物流、广告投放" : "International logistics / ad spend"} />
                  </label>
                  <label>
                    <span>{isZh ? "日期" : "Date"}</span>
                    <input type="date" value={expenseDraft.date} onChange={(event) => { setExpenseDraft((prev) => ({ ...prev, date: event.target.value })); }} />
                  </label>
                  <label>
                    <span>{isZh ? "金额" : "Amount"}</span>
                    <input type="number" min="0" step="0.01" value={expenseDraft.amount} onChange={(event) => { setExpenseDraft((prev) => ({ ...prev, amount: event.target.value })); }} placeholder="0.00" />
                  </label>
                  <label>
                    <span>{isZh ? "归属部门" : "Owner"}</span>
                    <input type="text" value={expenseDraft.owner} onChange={(event) => { setExpenseDraft((prev) => ({ ...prev, owner: event.target.value })); }} placeholder={isZh ? "采购 / 物流 / 市场" : "Procurement / logistics / marketing"} />
                  </label>
                  <label>
                    <span>{isZh ? "状态" : "Status"}</span>
                    <select value={expenseDraft.statusKey} onChange={(event) => { setExpenseDraft((prev) => ({ ...prev, statusKey: event.target.value })); }}>
                      {Object.entries(financeStatusLabels.expense).map(([key, label]) => (
                        <option key={key} value={key}>{label}</option>
                      ))}
                    </select>
                  </label>
                </div>
                <div className="admin-finance-editor-actions">
                  <button type="button" className="admin-finance-primary-action" onClick={submitExpenseDraft}>{isZh ? "加入账单" : "Add expense"}</button>
                </div>
              </article>
            ) : null}

            {financeWorkbenchSections.includes("receivable") ? (
              <article className="admin-finance-editor-card">
                <div className="admin-finance-editor-head">
                  <h5>{isZh ? "新增应收" : "Add Receivable"}</h5>
                  <span>{isZh ? "订单尾款 / 渠道账期 / 样品费" : "Order balances / channel terms / sample fees"}</span>
                </div>
                <div className="admin-finance-editor-fields">
                  <label>
                    <span>{isZh ? "客户" : "Customer"}</span>
                    <input type="text" value={receivableDraft.customer} onChange={(event) => { setReceivableDraft((prev) => ({ ...prev, customer: event.target.value })); }} placeholder={isZh ? "例如：华东渠道 / 海外客户" : "East region channel / overseas buyer"} />
                  </label>
                  <label>
                    <span>{isZh ? "应收单号" : "AR No."}</span>
                    <input type="text" value={receivableDraft.source} onChange={(event) => { setReceivableDraft((prev) => ({ ...prev, source: event.target.value })); }} placeholder="AR-001" />
                  </label>
                  <label>
                    <span>{isZh ? "到期日" : "Due Date"}</span>
                    <input type="date" value={receivableDraft.dueDate} onChange={(event) => { setReceivableDraft((prev) => ({ ...prev, dueDate: event.target.value })); }} />
                  </label>
                  <label>
                    <span>{isZh ? "金额" : "Amount"}</span>
                    <input type="number" min="0" step="0.01" value={receivableDraft.amount} onChange={(event) => { setReceivableDraft((prev) => ({ ...prev, amount: event.target.value })); }} placeholder="0.00" />
                  </label>
                  <label>
                    <span>{isZh ? "状态" : "Status"}</span>
                    <select value={receivableDraft.statusKey} onChange={(event) => { setReceivableDraft((prev) => ({ ...prev, statusKey: event.target.value })); }}>
                      {Object.entries(financeStatusLabels.receivable).map(([key, label]) => (
                        <option key={key} value={key}>{label}</option>
                      ))}
                    </select>
                  </label>
                </div>
                <div className="admin-finance-editor-actions">
                  <button type="button" className="admin-finance-primary-action" onClick={submitReceivableDraft}>{isZh ? "加入应收" : "Add receivable"}</button>
                </div>
              </article>
            ) : null}

            {financeWorkbenchSections.includes("payable") ? (
              <article className="admin-finance-editor-card">
                <div className="admin-finance-editor-head">
                  <h5>{isZh ? "新增应付" : "Add Payable"}</h5>
                  <span>{isZh ? "采购货款 / 物流费 / 平台费" : "Purchasing / freight / platform fees"}</span>
                </div>
                <div className="admin-finance-editor-fields">
                  <label>
                    <span>{isZh ? "供应商" : "Vendor"}</span>
                    <input type="text" value={payableDraft.vendor} onChange={(event) => { setPayableDraft((prev) => ({ ...prev, vendor: event.target.value })); }} placeholder={isZh ? "例如：供应商名称" : "Supplier name"} />
                  </label>
                  <label>
                    <span>{isZh ? "应付类型" : "Payable Type"}</span>
                    <input type="text" value={payableDraft.category} onChange={(event) => { setPayableDraft((prev) => ({ ...prev, category: event.target.value })); }} placeholder={isZh ? "采购货款 / 国际物流" : "Purchase payable / international freight"} />
                  </label>
                  <label>
                    <span>{isZh ? "到期日" : "Due Date"}</span>
                    <input type="date" value={payableDraft.dueDate} onChange={(event) => { setPayableDraft((prev) => ({ ...prev, dueDate: event.target.value })); }} />
                  </label>
                  <label>
                    <span>{isZh ? "金额" : "Amount"}</span>
                    <input type="number" min="0" step="0.01" value={payableDraft.amount} onChange={(event) => { setPayableDraft((prev) => ({ ...prev, amount: event.target.value })); }} placeholder="0.00" />
                  </label>
                  <label>
                    <span>{isZh ? "状态" : "Status"}</span>
                    <select value={payableDraft.statusKey} onChange={(event) => { setPayableDraft((prev) => ({ ...prev, statusKey: event.target.value })); }}>
                      {Object.entries(financeStatusLabels.payable).map(([key, label]) => (
                        <option key={key} value={key}>{label}</option>
                      ))}
                    </select>
                  </label>
                </div>
                <div className="admin-finance-editor-actions">
                  <button type="button" className="admin-finance-primary-action" onClick={submitPayableDraft}>{isZh ? "加入应付" : "Add payable"}</button>
                </div>
              </article>
            ) : null}

            {financeWorkbenchSections.includes("invoice") ? (
              <article className="admin-finance-editor-card">
                <div className="admin-finance-editor-head">
                  <h5>{isZh ? "新增发票" : "Add Invoice"}</h5>
                  <span>{isZh ? "销项 / 进项 / 费用票据" : "Sales / input / expense receipts"}</span>
                </div>
                <div className="admin-finance-editor-fields">
                  <label>
                    <span>{isZh ? "发票号" : "Invoice No."}</span>
                    <input type="text" value={invoiceDraft.invoiceNo} onChange={(event) => { setInvoiceDraft((prev) => ({ ...prev, invoiceNo: event.target.value })); }} placeholder="INV-001" />
                  </label>
                  <label>
                    <span>{isZh ? "类型" : "Type"}</span>
                    <input type="text" value={invoiceDraft.type} onChange={(event) => { setInvoiceDraft((prev) => ({ ...prev, type: event.target.value })); }} placeholder={isZh ? "销项发票 / 进项发票" : "Sales invoice / input invoice"} />
                  </label>
                  <label>
                    <span>{isZh ? "关联业务" : "Related Business"}</span>
                    <input type="text" value={invoiceDraft.related} onChange={(event) => { setInvoiceDraft((prev) => ({ ...prev, related: event.target.value })); }} placeholder={isZh ? "订单回款 / 采购成本" : "Order collection / purchase cost"} />
                  </label>
                  <label>
                    <span>{isZh ? "金额" : "Amount"}</span>
                    <input type="number" min="0" step="0.01" value={invoiceDraft.amount} onChange={(event) => { setInvoiceDraft((prev) => ({ ...prev, amount: event.target.value })); }} placeholder="0.00" />
                  </label>
                  <label>
                    <span>{isZh ? "状态" : "Status"}</span>
                    <select value={invoiceDraft.statusKey} onChange={(event) => { setInvoiceDraft((prev) => ({ ...prev, statusKey: event.target.value })); }}>
                      {Object.entries(financeStatusLabels.invoice).map(([key, label]) => (
                        <option key={key} value={key}>{label}</option>
                      ))}
                    </select>
                  </label>
                </div>
                <div className="admin-finance-editor-actions">
                  <button type="button" className="admin-finance-primary-action" onClick={submitInvoiceDraft}>{isZh ? "加入票据" : "Add invoice"}</button>
                </div>
              </article>
            ) : null}

            {financeWorkbenchSections.includes("reconciliation") ? (
              <article className="admin-finance-editor-card">
                <div className="admin-finance-editor-head">
                  <h5>{isZh ? "新增对账" : "Add Reconciliation"}</h5>
                  <span>{isZh ? "收款、付款、开票和税费核对" : "Match receipts, payments, invoices, and tax"}</span>
                </div>
                <div className="admin-finance-editor-fields">
                  <label>
                    <span>{isZh ? "对账科目" : "Subject"}</span>
                    <input type="text" value={reconciliationDraft.subject} onChange={(event) => { setReconciliationDraft((prev) => ({ ...prev, subject: event.target.value })); }} placeholder={isZh ? "例如：银行回单 / 待开票" : "Bank receipt / pending invoice"} />
                  </label>
                  <label>
                    <span>{isZh ? "金额" : "Amount"}</span>
                    <input type="number" min="0" step="0.01" value={reconciliationDraft.amount} onChange={(event) => { setReconciliationDraft((prev) => ({ ...prev, amount: event.target.value })); }} placeholder="0.00" />
                  </label>
                  <label>
                    <span>{isZh ? "状态" : "Status"}</span>
                    <select value={reconciliationDraft.statusKey} onChange={(event) => { setReconciliationDraft((prev) => ({ ...prev, statusKey: event.target.value })); }}>
                      {Object.entries(financeStatusLabels.reconciliation).map(([key, label]) => (
                        <option key={key} value={key}>{label}</option>
                      ))}
                    </select>
                  </label>
                </div>
                <div className="admin-finance-editor-actions">
                  <button type="button" className="admin-finance-primary-action" onClick={submitReconciliationDraft}>{isZh ? "加入对账" : "Add reconciliation"}</button>
                </div>
              </article>
            ) : null}

            {financeWorkbenchSections.includes("tax") ? (
              <article className="admin-finance-editor-card">
                <div className="admin-finance-editor-head">
                  <h5>{isZh ? "新增税项" : "Add Tax Item"}</h5>
                  <span>{isZh ? "销项、进项和所得税复核" : "Output, input, and income-tax review"}</span>
                </div>
                <div className="admin-finance-editor-fields">
                  <label>
                    <span>{isZh ? "税项" : "Tax Item"}</span>
                    <input type="text" value={taxDraft.taxItem} onChange={(event) => { setTaxDraft((prev) => ({ ...prev, taxItem: event.target.value })); }} placeholder={isZh ? "销项税 / 进项税" : "Output tax / input tax"} />
                  </label>
                  <label>
                    <span>{isZh ? "计税基数" : "Tax Base"}</span>
                    <input type="number" min="0" step="0.01" value={taxDraft.base} onChange={(event) => { setTaxDraft((prev) => ({ ...prev, base: event.target.value })); }} placeholder="0.00" />
                  </label>
                  <label>
                    <span>{isZh ? "税率(%)" : "Rate (%)"}</span>
                    <input type="number" min="0" step="0.01" value={taxDraft.rate} onChange={(event) => { setTaxDraft((prev) => ({ ...prev, rate: event.target.value })); }} placeholder="6" />
                  </label>
                  <label>
                    <span>{isZh ? "税额" : "Tax Amount"}</span>
                    <input type="number" min="0" step="0.01" value={taxDraft.amount} onChange={(event) => { setTaxDraft((prev) => ({ ...prev, amount: event.target.value })); }} placeholder="0.00" />
                  </label>
                  <label>
                    <span>{isZh ? "处理节点" : "Milestone"}</span>
                    <input type="text" value={taxDraft.dueDate} onChange={(event) => { setTaxDraft((prev) => ({ ...prev, dueDate: event.target.value })); }} placeholder={isZh ? "本月申报 / 季度复核" : "This filing / quarter review"} />
                  </label>
                  <label>
                    <span>{isZh ? "状态" : "Status"}</span>
                    <select value={taxDraft.statusKey} onChange={(event) => { setTaxDraft((prev) => ({ ...prev, statusKey: event.target.value })); }}>
                      {Object.entries(financeStatusLabels.tax).map(([key, label]) => (
                        <option key={key} value={key}>{label}</option>
                      ))}
                    </select>
                  </label>
                </div>
                <div className="admin-finance-editor-actions">
                  <button type="button" className="admin-finance-primary-action" onClick={submitTaxDraft}>{isZh ? "加入税项" : "Add tax item"}</button>
                </div>
              </article>
            ) : null}

            {financeWorkbenchSections.includes("budget") ? (
              <article className="admin-finance-editor-card">
                <div className="admin-finance-editor-head">
                  <h5>{isZh ? "新增预算" : "Add Budget"}</h5>
                  <span>{isZh ? "按部门记录预算执行" : "Track execution by department"}</span>
                </div>
                <div className="admin-finance-editor-fields">
                  <label>
                    <span>{isZh ? "部门" : "Department"}</span>
                    <input type="text" value={budgetDraft.department} onChange={(event) => { setBudgetDraft((prev) => ({ ...prev, department: event.target.value })); }} placeholder={isZh ? "采购 / 物流 / 营销" : "Procurement / logistics / marketing"} />
                  </label>
                  <label>
                    <span>{isZh ? "预算" : "Budget"}</span>
                    <input type="number" min="0" step="0.01" value={budgetDraft.budget} onChange={(event) => { setBudgetDraft((prev) => ({ ...prev, budget: event.target.value })); }} placeholder="0.00" />
                  </label>
                  <label>
                    <span>{isZh ? "已用" : "Used"}</span>
                    <input type="number" min="0" step="0.01" value={budgetDraft.used} onChange={(event) => { setBudgetDraft((prev) => ({ ...prev, used: event.target.value })); }} placeholder="0.00" />
                  </label>
                  <label>
                    <span>{isZh ? "状态" : "Status"}</span>
                    <select value={budgetDraft.statusKey} onChange={(event) => { setBudgetDraft((prev) => ({ ...prev, statusKey: event.target.value })); }}>
                      {Object.entries(financeStatusLabels.budget).map(([key, label]) => (
                        <option key={key} value={key}>{label}</option>
                      ))}
                    </select>
                  </label>
                </div>
                <div className="admin-finance-editor-actions">
                  <button type="button" className="admin-finance-primary-action" onClick={submitBudgetDraft}>{isZh ? "加入预算" : "Add budget"}</button>
                </div>
              </article>
            ) : null}

            {financeWorkbenchSections.includes("report") ? (
              <article className="admin-finance-editor-card">
                <div className="admin-finance-editor-head">
                  <h5>{isZh ? "新增报表任务" : "Add Report Task"}</h5>
                  <span>{isZh ? "日报、月报和年报导出任务" : "Daily, monthly, and annual export jobs"}</span>
                </div>
                <div className="admin-finance-editor-fields">
                  <label>
                    <span>{isZh ? "报表" : "Report"}</span>
                    <input type="text" value={reportDraft.report} onChange={(event) => { setReportDraft((prev) => ({ ...prev, report: event.target.value })); }} placeholder={isZh ? "现金流日报 / 月度利润表" : "Cashflow daily / monthly P&L"} />
                  </label>
                  <label>
                    <span>{isZh ? "周期" : "Period"}</span>
                    <input type="text" value={reportDraft.period} onChange={(event) => { setReportDraft((prev) => ({ ...prev, period: event.target.value })); }} placeholder={isZh ? "今日 / 2026-04 / 2026" : "Today / 2026-04 / 2026"} />
                  </label>
                  <label>
                    <span>{isZh ? "负责人" : "Owner"}</span>
                    <input type="text" value={reportDraft.owner} onChange={(event) => { setReportDraft((prev) => ({ ...prev, owner: event.target.value })); }} placeholder={isZh ? "财务经理 / 管理层" : "Finance manager / management"} />
                  </label>
                  <label>
                    <span>{isZh ? "导出格式" : "Export"}</span>
                    <input type="text" value={reportDraft.export} onChange={(event) => { setReportDraft((prev) => ({ ...prev, export: event.target.value })); }} placeholder="CSV / XLSX / PDF" />
                  </label>
                  <label>
                    <span>{isZh ? "状态" : "Status"}</span>
                    <select value={reportDraft.statusKey} onChange={(event) => { setReportDraft((prev) => ({ ...prev, statusKey: event.target.value })); }}>
                      {Object.entries(financeStatusLabels.report).map(([key, label]) => (
                        <option key={key} value={key}>{label}</option>
                      ))}
                    </select>
                  </label>
                </div>
                <div className="admin-finance-editor-actions">
                  <button type="button" className="admin-finance-primary-action" onClick={submitReportDraft}>{isZh ? "加入报表" : "Add report"}</button>
                </div>
              </article>
            ) : null}

            {financeWorkbenchSections.includes("approval") ? (
              <article className="admin-finance-editor-card">
                <div className="admin-finance-editor-head">
                  <h5>{isZh ? "新增审批" : "Add Approval"}</h5>
                  <span>{isZh ? "付款 / 退款 / 费用审计" : "Payment / refund / expense audit"}</span>
                </div>
                <div className="admin-finance-editor-fields">
                  <label>
                    <span>{isZh ? "审批类型" : "Type"}</span>
                    <input type="text" value={approvalDraft.type} onChange={(event) => { setApprovalDraft((prev) => ({ ...prev, type: event.target.value })); }} placeholder={isZh ? "例如：付款审批" : "Payment approval"} />
                  </label>
                  <label>
                    <span>{isZh ? "事项" : "Subject"}</span>
                    <input type="text" value={approvalDraft.subject} onChange={(event) => { setApprovalDraft((prev) => ({ ...prev, subject: event.target.value })); }} placeholder={isZh ? "例如：采购货款 / 营销超支" : "Purchase payment / overspend"} />
                  </label>
                  <label>
                    <span>{isZh ? "金额" : "Amount"}</span>
                    <input type="number" min="0" step="0.01" value={approvalDraft.amount} onChange={(event) => { setApprovalDraft((prev) => ({ ...prev, amount: event.target.value })); }} placeholder="0.00" />
                  </label>
                  <label>
                    <span>{isZh ? "负责人" : "Owner"}</span>
                    <input type="text" value={approvalDraft.owner} onChange={(event) => { setApprovalDraft((prev) => ({ ...prev, owner: event.target.value })); }} placeholder={isZh ? "财务经理 / 部门负责人" : "Finance manager / department owner"} />
                  </label>
                  <label>
                    <span>{isZh ? "状态" : "Status"}</span>
                    <select value={approvalDraft.statusKey} onChange={(event) => { setApprovalDraft((prev) => ({ ...prev, statusKey: event.target.value })); }}>
                      {Object.entries(financeStatusLabels.approval).map(([key, label]) => (
                        <option key={key} value={key}>{label}</option>
                      ))}
                    </select>
                  </label>
                </div>
                <div className="admin-finance-editor-actions">
                  <button type="button" className="admin-finance-primary-action" onClick={submitApprovalDraft}>{isZh ? "加入审批流" : "Add approval"}</button>
                </div>
              </article>
            ) : null}
          </div>
        </section>
      ) : null}

      <section className="admin-insights-grid">
        <InsightPanel
          title={isZh ? "关键驱动拆解" : "Key Driver Breakdown"}
          description={isZh ? "用可视化条形结构快速判断哪个环节最值得继续优化。" : "Use a compact bar view to see which lever deserves attention next."}
        >
          <InsightBarList rows={config.bars} />
        </InsightPanel>

        <InsightPanel
          title={isZh ? "本周执行节奏" : "This Week's Operating Rhythm"}
          description={isZh ? "把策略落成能执行的动作，而不是只停留在数据看板。" : "Translate strategy into execution steps instead of leaving it as a static dashboard."}
          aside={<span className="admin-insights-aside-tag">{isZh ? "行动面板" : "Action Panel"}</span>}
        >
          <InsightTimeline items={config.timeline} />
        </InsightPanel>
      </section>

      {config.records ? (
        <InsightPanel
          title={config.records.title}
          description={config.records.description}
          aside={<span className="admin-insights-aside-tag">{isZh ? "账单记录" : "Billing Records"}</span>}
        >
          <div className="table-wrap">
            <table className="controlhub-table">
              <thead>
                <tr>
                  {config.records.columns.map((column) => (
                    <th key={column.key}>{column.label}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {config.records.rows.map((row, rowIndex) => (
                  <tr key={`finance-record-${adminTab}-${rowIndex}`}>
                    {config.records.columns.map((column) => (
                      <td key={`${column.key}-${rowIndex}`}>{typeof column.render === "function" ? column.render(row) : (row[column.key] ?? "-")}</td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </InsightPanel>
      ) : null}
    </section>
  );
}

export function AdminSettingsHubPage({ locale, systemConfigDraft, savingSystemConfig, systemConfigForm }) {
  const isZh = locale === "zh";
  const mysqlHost = String(systemConfigDraft?.mysql?.host || "-");
  const mysqlDb = String(systemConfigDraft?.mysql?.database || "-");
  const redisEnabled = Boolean(systemConfigDraft?.redis?.enabled);
  const adminEmail = String(systemConfigDraft?.email?.user || systemConfigDraft?.email?.from || "-");

  return (
    <section className="admin-insights-page admin-settings-page">
      <article className="admin-insights-hero admin-settings-hero">
        <div>
          <p className="admin-insights-eyebrow">{isZh ? "系统配置中心" : "System Configuration Center"}</p>
          <h3>{isZh ? "系统设置" : "System Settings"}</h3>
          <p>{isZh ? "在统一壳层里重组运行配置、邮件与缓存信息，保留原有保存逻辑和表单能力。" : "Reframe runtime configuration, mail, and cache settings inside the new shell while keeping the original save behavior intact."}</p>
        </div>
        <div className="admin-insights-hero-chip">
          <span>{isZh ? "保存状态" : "Save State"}</span>
          <strong>{savingSystemConfig ? (isZh ? "保存中" : "Saving") : (isZh ? "可编辑" : "Editable")}</strong>
        </div>
      </article>

      <section className="admin-insights-metric-grid">
        <InsightMetric title={isZh ? "MySQL 主机" : "MySQL Host"} value={mysqlHost} detail={mysqlDb} tone="blue" />
        <InsightMetric title="Redis" value={redisEnabled ? (isZh ? "已启用" : "Enabled") : (isZh ? "未启用" : "Disabled")} detail={isZh ? "缓存与会话状态" : "Cache and session state"} tone="violet" />
        <InsightMetric title={isZh ? "邮件身份" : "Mail Identity"} value={adminEmail} detail={isZh ? "通知与发件配置" : "Notification and sender setup"} tone="emerald" />
        <InsightMetric title={isZh ? "配置范围" : "Config Scope"} value={isZh ? "运行时配置" : "Runtime Config"} detail={isZh ? "数据库、邮件、Redis 等" : "Database, mail, Redis and more"} tone="amber" />
      </section>

      <section className="admin-insights-grid admin-settings-layout">
        <InsightPanel
          title={isZh ? "配置总览" : "Configuration Overview"}
          description={isZh ? "先看环境关键位，再进入原有表单逐项调整。" : "Review critical environment values first, then adjust fields in the original form."}
        >
          <div className="admin-settings-summary-list">
            <div><span>MySQL</span><strong>{`${mysqlHost} / ${mysqlDb}`}</strong></div>
            <div><span>Redis</span><strong>{redisEnabled ? (isZh ? "启用中" : "Enabled") : (isZh ? "关闭" : "Disabled")}</strong></div>
            <div><span>{isZh ? "邮件账号" : "Mail Account"}</span><strong>{adminEmail}</strong></div>
            <div><span>{isZh ? "配置提示" : "Config Note"}</span><strong>{isZh ? "保留现有保存逻辑" : "Original save flow preserved"}</strong></div>
          </div>
        </InsightPanel>

        <InsightPanel
          title={isZh ? "运维提示" : "Operations Notes"}
          description={isZh ? "这一栏给管理员一个更像 SaaS 后台的解释层。" : "Provide admins with a clearer SaaS-style interpretation layer."}
          aside={<span className="admin-insights-aside-tag">{isZh ? "只读" : "Read Only"}</span>}
        >
          <InsightTimeline
            items={[
              { title: isZh ? "先校验数据库连接项" : "Validate database settings first", detail: isZh ? "主机、端口、数据库名应与部署环境保持一致。" : "Host, port, and database name should match deployment.", tone: "blue" },
              { title: isZh ? "再检查邮件发件身份" : "Review outbound mail identity", detail: isZh ? "发件人、用户名与 SMTP 策略要统一。" : "Sender, username, and SMTP policy must align.", tone: "amber" },
              { title: isZh ? "最后确认缓存策略" : "Confirm cache strategy last", detail: isZh ? "Redis 开关会影响缓存与部分运行体验。" : "Redis affects cache behavior and parts of runtime UX.", tone: "emerald" }
            ]}
          />
        </InsightPanel>
      </section>

      <div className="admin-settings-form-host">{systemConfigForm}</div>
    </section>
  );
}